// ─────────────────────────────────────────────────────────────
// IMPORTS
// Three.js é a biblioteca principal de 3D no browser.
// GUI é um painel de controles visuais (aquele painel no canto da tela).
// GLTFLoader carrega modelos 3D no formato .gltf / .glb (padrão do Blender).
// RoomEnvironment gera um "estúdio de foto" virtual para reflexos PBR.
// ─────────────────────────────────────────────────────────────
import * as THREE from 'three'
import GUI from 'lil-gui'
import { GLTFLoader } from 'three/examples/jsm/Addons.js'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js'
// BVH: raycast na ilha (~106k tris) + Mac (~77k) em <0.1ms, em vez de varrer triângulo a triângulo
import { computeBoundsTree, acceleratedRaycast } from 'three-mesh-bvh'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'   // mobile: palavras que "acendem" e linhas da "dobradiça"
import Lenis from 'lenis'
// URL do WASM do Rive servida pelo PRÓPRIO domínio (Vite faz o bundle do asset).
// Sem isto o @rive-app/webgl2 baixa o rive.wasm (~2MB) do unpkg.com em runtime —
// era o que travava os .riv por vários segundos. ?url = só a string da URL, não
// puxa o runtime do Rive pro bundle principal (continua lazy via import()).
import riveWasmUrl from '@rive-app/webgl2/rive.wasm?url'

// Começa a baixar o WASM do Rive JÁ, em vez de esperar o 1º Rive instanciar. Como é
// o asset compartilhado por TODOS os .riv, tê-lo no cache faz os rives renderizarem
// assim que aparecem. priority 'low': cede banda p/ o 3D, que é o que segura o loader.
fetch(riveWasmUrl, { cache: 'force-cache', priority: 'low' }).catch(() => {})
// O CTA do nav precisa aparecer junto com o reveal: o .riv dele (~80KB) também já
// vem pro cache agora — senão ele chega ~2s depois, atrás do pré-download dos cards.
fetch('/riv/CTA.riv', { cache: 'force-cache', priority: 'low' }).catch(() => {})

// Resolvido no onLoad do CTA (initCtaRive). O pré-download dos .riv dos cards espera
// por ele p/ não disputar banda com o botão do nav.
let resolveCtaReady
const ctaReady = new Promise((r) => { resolveCtaReady = r })

// ?debug na URL → painel lil-gui visível + zoom da câmera. Sem a flag, `gui` é um
// stub que engole qualquer cadeia de chamadas (gui.addFolder().add().name()...),
// então os controles espalhados pelo arquivo não precisam de if(DEBUG).
const DEBUG = new URLSearchParams(location.search).has('debug')
const noopGui = new Proxy(function () {}, { get: () => noopGui, apply: () => noopGui })

// Celular/tablet em retrato (≤768px): mesmo 3D, mais leve, com coreografia e layout próprios
// (ver "MOBILE — coreografia"). Lido 1x no load — girar p/ paisagem não troca.
const IS_MOBILE = window.matchMedia('(max-width: 768px)').matches
// "Reduzir movimento" do sistema: no mobile a coreografia vira fades simples (sem queda/giro
// nem travessia do MOTION)
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches

gsap.registerPlugin(ScrollTrigger, SplitText)
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree
THREE.Mesh.prototype.raycast = acceleratedRaycast

// ─────────────────────────────────────────────────────────────
// RESET DE SCROLL NO RELOAD
// Ao atualizar a página o user deve voltar SEMPRE p/ a 1ª section (hero),
// nunca para a posição onde estava. Fica no topo do módulo (antes de qualquer
// código que possa atrasar/falhar) p/ garantir que sempre executa:
//   • manual  → desliga a restauração de scroll nativa do browser
//   • scrollTo(0,0) agora
// (O Lenis começa parado — o scroll fica travado enquanto o loader está na tela.)
// ─────────────────────────────────────────────────────────────
if ('scrollRestoration' in history) history.scrollRestoration = 'manual'
window.scrollTo(0, 0)

// ─────────────────────────────────────────────────────────────
// ABERTURA DA TAMPA (lid) DO MACBOOK
// Só roda quando as DUAS coisas acontecem: o site foi revelado
// (startSiteIntro, disparado pelo loader) e o modelo terminou de carregar.
// ─────────────────────────────────────────────────────────────
let siteIntroStarted = false
let lidLoaded = false
const LID_OPEN = -1.7215   // rotação X da tampa aberta (node Bevels_2)

function tryStartLidAnim() {
    if (!siteIntroStarted || !lidLoaded) return
    if (IS_MOBILE) return   // mobile: a tampa faz parte da timeline da coreografia (playMobileIntro)
    replayLidAnim()
}

// Hero title é um SVG (MOTION): no corte cada letra (M O T I O N) cai separadamente, com blur.
// Mobile: o MOTION vira uma <svg> por letra (viewBox recortado na letra) dentro de um <div> —
// a faixa, que a "câmera" atravessa com o scroll (ver "MOBILE — coreografia"). Cada letra é uma
// camada composta → a queda com blur roda na GPU; em path de SVG, transform/blur re-rasterizam a
// faixa inteira (~2600×540px físicos) a cada frame no iPhone. O wrapper .hero-motion recorta.
const SVG_NS = 'http://www.w3.org/2000/svg'
function splitHeroTitle() {
    const svg = document.querySelector('.hero-title')
    if (!svg) return
    const [, , vbW, vbH] = svg.getAttribute('viewBox').split(' ').map(Number)
    const band = document.createElement('div')
    band.className = 'hero-title'
    band.setAttribute('role', 'img')
    band.setAttribute('aria-label', svg.getAttribute('aria-label') || 'MOTION')
    for (const path of [...svg.querySelectorAll('path')]) {
        const { x, width } = path.getBBox()
        const letter = document.createElementNS(SVG_NS, 'svg')
        letter.setAttribute('class', 'hero-letter')
        letter.setAttribute('viewBox', `${x} 0 ${width} ${vbH}`)
        letter.setAttribute('aria-hidden', 'true')
        letter.style.left  = `${x / vbW * 100}%`
        letter.style.width = `${width / vbW * 100}%`
        letter.appendChild(path)
        band.appendChild(letter)
    }
    svg.replaceWith(band)
}
if (IS_MOBILE) splitHeroTitle()
const heroTitleEl  = document.querySelector('.hero-title')
const heroMotionEl = document.querySelector('.hero-motion')
// ordem de leitura: M → O → T → I → O → N (desktop: os paths; mobile: as <svg> das letras)
const heroLetters  = gsap.utils.toArray(IS_MOBILE ? '.hero-letter' : '.hero-title path')
gsap.set(heroLetters, { opacity: 0 })                        // esconde antes da intro (evita flash)

// Os 2 textos pequenos do hero (bio esq/dir) — fade-in na intro. (Mobile: ficam abaixo da
// dobra, sempre visíveis; acendem palavra a palavra com o scroll — ver litOnScroll.)
const heroBios = gsap.utils.toArray('.bio-text-left, .bio-text-right')
if (!IS_MOBILE) gsap.set(heroBios, { opacity: 0 })          // escondidos até o loader sair

// Chamado no CORTE do loader p/ o site (cutToSite).
function startSiteIntro() {
    siteIntroStarted = true
    // mobile: coreografia própria (ver "MOBILE — coreografia"), que também destrava o scroll
    if (IS_MOBILE) { playMobileIntro(); return }
    releaseScroll()
    gsap.to(introOffset, {
        y: 0,
        duration: 2,
        ease: makeCubicBezier(0.16, 1, 0.3, 1),
    })
    gsap.to(islandGroup.scale, {
        x: 1.18, y: 1.18, z: 1.18,
        duration: 2,
        ease: makeCubicBezier(0.16, 1, 0.3, 1),
    })
    // O MOTION (letra a letra) NÃO entra aqui: ele é disparado por playHeroTitleIntro()
    // só quando o overlay do loader é removido — senão a animação rodaria escondida atrás dele.
    tryStartLidAnim()
}

// MOTION letra a letra (desktop): desce 180px (px de tela), blur que se desfaz no caminho e
// opacidade curtinha; stagger de 2 frames entre as letras. Chamado no corte do loader.
// (Mobile: a mesma queda, dentro da timeline da coreografia — ver motionLettersDrop.)
let heroTitlePlayed = false
function playHeroTitleIntro() {
    if (heroTitlePlayed || IS_MOBILE) return
    heroTitlePlayed = true
    const FRAME = 1 / 60                                         // 1 frame ≈ 16.6ms
    const titleW = heroTitleEl ? heroTitleEl.getBoundingClientRect().width : 0
    if (titleW > 0) {                                            // (width 0 → DROP Infinity)
        const scale = titleW / 1226                             // px de tela por unidade do viewBox
        const DROP  = 180 / scale                               // 180px de tela → unidades do SVG
        gsap.timeline()
            .fromTo(heroLetters,
                { y: -DROP, filter: 'blur(12px)' },
                { y: 0, filter: 'blur(0px)', duration: 1, ease: makeCubicBezier(0, 0, 0, 1), stagger: FRAME * 2, clearProps: 'filter' },
                0)
            .fromTo(heroLetters,
                { opacity: 0 },
                { opacity: 1, duration: 0.08, ease: 'none', stagger: FRAME * 2 },   // fade curto, ~linear
                0)
    }

    // Textos pequenos (bio): fade-in começando 100ms após o fim do loading, durando 400ms
    gsap.to(heroBios, { opacity: 1, duration: 0.4, delay: 0.1, ease: 'power2.out' })
}

// O reveal (startSiteIntro) é disparado pelo LOADER quando os assets 3D
// terminam de carregar — ver "revealSite" / LoadingManager mais abaixo.

// ─────────────────────────────────────────────────────────────
// GUI (painel de debug)
// ─────────────────────────────────────────────────────────────
const gui = DEBUG ? new GUI() : noopGui

const guiPosParams = { top: 0, right: 0 }
const guiPosFolder = gui.addFolder('↔ posição do GUI')
guiPosFolder.add(guiPosParams, 'top', 0, window.innerHeight - 50, 1).name('top (px)').onChange(v => {
    gui.domElement.style.top = v + 'px'
})
guiPosFolder.add(guiPosParams, 'right', 0, window.innerWidth - 50, 1).name('right (px)').onChange(v => {
    gui.domElement.style.right = v + 'px'
    gui.domElement.style.left  = 'auto'
})

// ─────────────────────────────────────────────────────────────
// BASE
// ─────────────────────────────────────────────────────────────
const canvas = document.querySelector('canvas.webgl')

// Pré-carrega os cursores SVG → 1º hover na ilha/mac não espera o download
// (o browser só buscaria cada cursor no primeiro uso, daí o delay). Só com mouse: no
// toque não existe cursor.
if (window.matchMedia('(pointer: fine)').matches) {
    ;['defaultcursor', 'openhand', 'closedhand', 'pointinghand'].forEach((name) => {
        const img = new Image()
        img.src = `/cursors/${name}.svg`
    })
}

const scene = new THREE.Scene()

// ── LOADER ─────────────────────────────────────────────────────
// Segura tudo atrás de um overlay até os assets 3D (ilha + macbook +
// texturas) terminarem de carregar; aí revela e dispara a intro junto.
// No mobile (≤768px) o 3D é o mesmo, mais leve: ilha simplificada (island-mobile.glb),
// pixel ratio/sombra menores, ilha enquadrada p/ retrato (fitIslandMobile). Ver usos de IS_MOBILE.
const loadingManager   = new THREE.LoadingManager()
const loaderEl         = document.getElementById('loader')
const loaderCount      = document.getElementById('loader-count')
const loaderCountRight = document.getElementById('loader-count-right')
const loaderVideo      = document.getElementById('loader-video')
const loaderLines      = document.getElementById('loader-lines')

// ─── AUTOPLAY RESILIENTE (todos os vídeos são decorativos e mudos) ───
// No iPhone o próprio sistema pode negar o autoplay até de vídeo mudo: Modo Pouca
// Energia e aparelho quente (mitigação térmica do WebKit). Aí o play() é rejeitado
// (NotAllowedError) e o Safari desenharia um botão de play por cima (escondido no CSS).
// Guardamos esses vídeos e tentamos de novo no 1º gesto (toque/clique/tecla), que o
// iOS aceita. data-autoplay="off" = não deve mais tocar (ex.: card saiu da tela).
const blockedVideos = new Set()
const autoplayStatus = { estado: 'ok' }   // ?debug (mobile): mostra se o sistema bloqueou
function playVideo(v) {
    if (!v) return
    v.muted = true
    const p = v.play()
    if (p) p.then(() => blockedVideos.delete(v), (err) => {
        if (err?.name !== 'NotAllowedError') return      // AbortError etc. (pause/load no meio) não é bloqueio
        blockedVideos.add(v)
        autoplayStatus.estado = 'bloqueado pelo sistema'
    })
}
function retryBlockedVideos() {
    for (const v of blockedVideos) {
        if (v.dataset.autoplay === 'off') { blockedVideos.delete(v); continue }
        v.play().then(() => blockedVideos.delete(v), () => {})
    }
}
;['touchend', 'click', 'keydown'].forEach((t) => window.addEventListener(t, retryBlockedVideos, { capture: true, passive: true }))
playVideo(loaderVideo)   // reforça o play() inline do HTML; se o sistema negar, o retry acima assume

// (centragem do vídeo é feita por flex no #loader-video-wrap; o GSAP usa a
//  transform só p/ o slide — não centralizamos via GSAP p/ não dobrar o offset)

// % no formato "000034%" (6 dígitos + "%") nos dois contadores. Nunca volta: o LoadingManager
// soma as texturas embutidas dos GLBs no meio do caminho (o total cresce → 80% virava 63%).
let loaderPct = 0
function setLoaderCount(pct) {
    loaderPct = Math.max(loaderPct, Math.round(pct))
    const txt = String(loaderPct).padStart(6, '0') + '%'
    if (loaderCount)      loaderCount.textContent      = txt
    if (loaderCountRight) loaderCountRight.textContent = txt
}

loadingManager.onProgress = (url, loaded, total) => {
    setLoaderCount((loaded / total) * 100)
}

// Saída do loader (faz parte do design — roda depois de chegar a 100%): 2 linhas (vídeo com
// alpha) descem aceleradas por cima do loading até a borda de baixo, e o loading desliza 10% da
// própria altura com bezier(1,0,1,1), SUBINDO (câmera descendo) — acelera até o fim.
// O CORTE p/ o site é um match cut: cai no último frame das linhas = pico de velocidade do
// slide, e o site entra já em movimento, no trecho mais rápido das curvas: as letras do MOTION
// caindo (0,0,0,1) e a ilha girando no lugar (0.16,1,0.3,1) — igual no desktop e no mobile. Por
// isso UMA timeline conduz tudo e começa no 1º frame das linhas na tela (o 'ended' do vídeo
// chegava atrasado no iPhone: o slide parava no pico e a tela "travava" antes do corte).
const SLIDE_DUR = 0.5                      // duração do slide (s)
const SLIDE_Y   = -10                      // % da altura do vídeo, p/ cima (sutil, não esvazia a tela)
// Só TRUE quando o loader sai → libera o cursor de "mover 3D" (openhand) e o drag.
// Antes disso o site fica só com o cursor default, mesmo passando sobre a ilha.
let interactionReady = false
// Enquanto o loader (opaco) cobre o canvas o 3D não renderiza — só os frames de aquecimento
// quando os GLBs chegam (compila shaders, sobe texturas). Sobra GPU p/ os vídeos do loader.
let loaderCovers = true
let warmupFrames = 0

// O CORTE. Roda dentro do gsap.ticker (callback da timeline da saída); o tick() do 3D roda
// logo depois, no mesmo frame → o 1º frame do site já mostra a pose nova da ilha.
let siteCut = false
function cutToSite() {
    if (siteCut) return
    siteCut = true
    interactionReady = true   // libera cursor "open hand" / drag do 3D só agora
    loaderCovers = false
    const scrolled = window.scrollY   // lido antes de mexer em estilo (não força layout)
    // esconder é barato; tirar do DOM e liberar os 2 vídeos fica p/ depois, fora do corte
    if (loaderEl) loaderEl.style.visibility = 'hidden'
    for (const v of [loaderVideo, loaderLines]) {
        if (!v) continue
        v.dataset.autoplay = 'off'   // não voltam a tocar no retry de autoplay
        v.pause()
    }
    // (o teclado ainda rola a página por trás do loader — o Lenis só segura roda e toque)
    if (scrolled) lenis.scrollTo(0, { immediate: true, force: true })
    startSiteIntro()         // ilha + Mac (mobile: toda a coreografia) começam SÓ agora
    playHeroTitleIntro()     // MOTION letra a letra (desktop)
    setTimeout(disposeLoader, 2000)
}
// Libera os decoders dos vídeos do loader (o iPhone tem poucos) e tira o overlay do DOM.
function disposeLoader() {
    for (const v of [loaderVideo, loaderLines]) {
        if (!v) continue
        v.querySelectorAll('source').forEach((src) => src.remove())
        v.removeAttribute('src')
        v.load()
    }
    loaderEl?.remove()
}

function runLoaderOutro() {
    // duração real do vídeo de linhas (fallback 0.7s = 21 frames @30fps)
    const linesDur = (loaderLines && loaderLines.duration > 0) ? loaderLines.duration : 0.7
    const outro = gsap.timeline({ paused: true })
        .to('.loader-count', { opacity: 0, duration: 0.4, ease: 'power2.out' }, 0.2)   // números só dão fade
        .call(cutToSite, null, linesDur)
    // slide que TERMINA no corte (começa atrasado: linhas − slide)
    if (loaderVideo) outro.to(loaderVideo, {
        yPercent: SLIDE_Y, duration: SLIDE_DUR, ease: makeCubicBezier(1, 0, 1, 1),
    }, Math.max(0, linesDur - SLIDE_DUR))

    // Começa no 1º frame das linhas NA TELA (o vídeo leva alguns frames p/ começar), já no
    // tempo de mídia desse frame. Se o sistema negar o autoplay (Modo Pouca Energia) ou o
    // vídeo demorar, segue sem ele em 300ms (no relógio do GSAP: com a aba oculta, espera
    // o user voltar — a saída nunca acontece sem ninguém vendo).
    let started = false
    const start = (now, meta) => {
        if (started) return
        started = true
        outro.play(Math.min(meta?.mediaTime ?? 0, 0.1))
    }
    if (loaderLines) {
        loaderLines.style.opacity = '1'
        try { loaderLines.currentTime = 0 } catch (e) {}
        if ('requestVideoFrameCallback' in loaderLines) loaderLines.requestVideoFrameCallback(start)
        else loaderLines.addEventListener('playing', () => start(), { once: true })
        playVideo(loaderLines)
    }
    gsap.delayedCall(0.3, start)
}

let siteRevealed = false
function revealSite() {
    if (siteRevealed) return
    siteRevealed = true
    setLoaderCount(100)                 // 000100
    startRives()                        // no-op se o onLoad já iniciou (fallback)
    runLoaderOutro()                    // linhas + slide; no fim, o corte → startSiteIntro

    // Pré-baixa os .riv dos cards depois do reveal, p/ estarem no cache quando o
    // scroll chegar neles (ai_widget_builder_header é o maior, ~3MB).
    // No mobile (≤768px) type_of_work (Services é HTML), motion_card e dynamic_card não
    // são instanciados — não adianta pré-baixá-los (só gastaria dados); ficam de fora.
    const RIVE_FILES = [
        ...(IS_MOBILE ? [] : ['/riv/type_of_work(production).riv', '/riv/motion_card.riv', '/riv/dynamic_card.riv']),
        '/riv/ai_widget_builder_header.riv',
        '/riv/sample_conversations.riv',
        '/riv/shareable_and_reusable.riv',
    ]

    // Os vídeos da grade NÃO entram aqui: cada um só começa a baixar quando o card
    // chega perto da tela (ver "PERF: vídeos dos cards" no fim do arquivo).
    const prefetchRives = () => {
        RIVE_FILES.forEach((u) => fetch(u, { cache: 'force-cache', priority: 'low' }).catch(() => {}))
    }
    // só depois do CTA (ou 3s, se ele travar) — ver ctaReady no topo — e, no mobile,
    // depois da coreografia do hero (mobileIntroDone; no desktop já vem resolvida)
    const waitCta = Promise.race([ctaReady, new Promise((r) => setTimeout(r, 3000))])
    Promise.all([waitCta, mobileIntroDone]).then(() => {
        if ('requestIdleCallback' in window) requestIdleCallback(prefetchRives, { timeout: 1500 })
        else setTimeout(prefetchRives, 300)
    })
}
// O loader segura até DUAS coisas: (1) os assets 3D (LoadingManager) e (2) o vídeo
// da tela do MacBook estar pronto p/ tocar — assim o loading esconde o load da tela
// (antes ela aparecia preta/carregando depois do reveal). screenReady é setado lá
// embaixo, onde screenVideoEl é criado (com 'canplay'/'error').
let assets3DReady = false
let screenReady   = IS_MOBILE   // mobile não espera a tela: ela só acende no fim da coreografia
function tryReveal() { if (assets3DReady && screenReady) revealSite() }
loadingManager.onLoad = () => {
    assets3DReady = true
    warmupFrames = 2          // renderiza 2 frames sob o loader: shaders compilados antes do corte
    startRives()
    tryReveal()
    // mobile, rede lenta: o fallback revelou o site sem a ilha → ela entra agora (só ela)
    if (mobileAwait3D) { mobileAwait3D = false; renderer.compile(scene, camera); playMobileIntro(true) }
}
// fallback: revela mesmo se algum asset travar (mobile mais curto — rede de celular
// ruim não pode prender o usuário no loading; a ilha aparece quando chegar)
setTimeout(revealSite, IS_MOBILE ? 6000 : 12000)
if (DEBUG) window.__reveal = revealSite   // ?debug: força o reveal (testes em aba oculta, sem vídeo/timers)

const gltfLoader = new GLTFLoader(loadingManager)
gltfLoader.setMeshoptDecoder(MeshoptDecoder) // p/ o GLB comprimido (EXT_meshopt_compression)

// ─────────────────────────────────────────────────────────────
// GRUPO: ILHA + MACBOOK
// ─────────────────────────────────────────────────────────────
const islandGroup = new THREE.Group()
islandGroup.rotation.set(0.2, -0.07, 0)
islandGroup.position.set(0.01, -0.1, 0)
// desktop: começa 10% maior e a intro encolhe p/ 1.18. Mobile: já 1.18 — a escala da
// coreografia fica no mobileHero.scale (aplicada no mobileFit), não aqui.
islandGroup.scale.setScalar(IS_MOBILE ? 1.18 : 1.18 * 1.1)

// Grupo por FORA do islandGroup: no celular (retrato) encolhe a ilha até caber na
// largura e a posiciona (repouso no centro entre o nav e o MOTION — ver fitIslandMobile).
// Separado p/ não brigar com a intro (que anima a escala do islandGroup) nem com o drag.
// No desktop = identidade.
const mobileFit = new THREE.Group()
mobileFit.add(islandGroup)
scene.add(mobileFit)

// Controles GUI
const gf = gui.addFolder('Grupo (Ilha + Mac)')
gf.add(islandGroup.position, 'x', -10, 10, 0.01).name('pos X')
gf.add(islandGroup.position, 'y', -10, 10, 0.01).name('pos Y')
gf.add(islandGroup.position, 'z', -10, 10, 0.01).name('pos Z')
gf.add(islandGroup.rotation, 'x', -Math.PI, Math.PI, 0.01).name('rot X')
gf.add(islandGroup.rotation, 'y', -Math.PI, Math.PI, 0.01).name('rot Y')
gf.add(islandGroup.rotation, 'z', -Math.PI, Math.PI, 0.01).name('rot Z')
// onChange garante que X, Y e Z da escala mudem juntos (escala uniforme)
gf.add(islandGroup.scale, 'x', 0.01, 5, 0.01).name('scale').onChange(v => islandGroup.scale.set(v, v, v))
gf.close()

// ─────────────────────────────────────────────────────────────
// MODELO: MACBOOK
// gltfLoader.load() é assíncrono — o código dentro do callback
// só roda quando o arquivo terminar de carregar.
// ─────────────────────────────────────────────────────────────
let macbook = null
let lid = null
// screenAnchor: objeto invisível no centro da tela do MacBook (filho da tampa) — posiciona a
// luz da tela (screenLight) a cada frame. screenMaterial: material da tela (Object_7) — no
// mobile a cor dele vai de preto a branco quando a tela "acende" (mobileHero.power).
let screenAnchor = null
let screenMaterial = null

gltfLoader.load('/models/macbook.glb', (gltf) =>
{
    macbook = gltf.scene // gltf.scene contém toda a hierarquia de objetos do modelo

    macbook.scale.setScalar(2.542)              // tamanho do modelo
    macbook.position.set(0.02, 0.5, 0)          // posição dentro do grupo
    macbook.rotation.set(0.2084, 0.59, -0.1159) // rotação ajustada manualmente no GUI
    macbook.updateWorldMatrix(true, true)        // força atualização das matrizes internas

    islandGroup.add(macbook) // adiciona o macbook dentro do grupo da ilha

    // screenAnchor: ponto de referência no centro da tela do MacBook (posiciona a
    // screenLight). É filho do lid (tampa), então acompanha a rotação dela.
    // attach() reparenta preservando o world transform — encaixe não muda.
    screenAnchor = new THREE.Object3D()
    screenAnchor.position.set(0, 0.1125, -0.123)
    screenAnchor.rotation.x = -0.1465
    macbook.add(screenAnchor)

    const sf = gui.addFolder('Âncora da tela (luz)')
    sf.add(screenAnchor.position, 'x', -5, 5, 0.001).name('pos X')
    sf.add(screenAnchor.position, 'y', -5, 5, 0.001).name('pos Y')
    sf.add(screenAnchor.position, 'z', -5, 5, 0.001).name('pos Z')
    sf.add(screenAnchor.rotation, 'x', -Math.PI, Math.PI, 0.001).name('rot X')
    sf.add(screenAnchor.rotation, 'y', -Math.PI, Math.PI, 0.001).name('rot Y')
    sf.add(screenAnchor.rotation, 'z', -Math.PI, Math.PI, 0.001).name('rot Z')
    sf.close()

    // ── Tela do MacBook ────────────────────────────────────────
    // Object_7 é o quad da tela (16:10, UV 0→1). O vídeo vira uma VideoTexture
    // direto nele — oclusão/antialias corretos pelo próprio WebGL. (Antes era um
    // <video> CSS3D atrás do canvas, que "vazava" pelas bordas ao girar a ilha.)
    // polygonOffset: o vidro da tampa (Object_6) fica ~0.0003 atrás da tela, quase
    // coplanar — o offset garante que a tela sempre ganha no depth (sem piscar).
    macbook.traverse((child) => {
        if (child.name === 'Object_7') {
            child.material = new THREE.MeshBasicMaterial({
                map: screenTexture,
                toneMapped: false,          // igual ao <video> no DOM (sem ACES)
                side: THREE.DoubleSide,
                polygonOffset: true,
                polygonOffsetFactor: -1,
                polygonOffsetUnits: -4,
            })
            screenMaterial = child.material   // mobile: a cor dele "acende" a tela (mobileHero.power)
        }
    })

    // ── Sombras + BVH (raycast rápido p/ hover/drag) ───────────
    macbook.traverse((child) => {
        if (child.isMesh) {
            child.castShadow = true
            child.receiveShadow = true // necessário para receber o brilho da screenLight
            child.geometry.computeBoundsTree()
        }
    })

    // ── Abre a tampa ───────────────────────────────────────────
    // 'Bevels_2' é o nome do node da tampa/tela no arquivo GLTF
    macbook.traverse((child) => {
        if (child.name === 'Bevels_2') lid = child
    })
    if (lid) {
        lid.rotation.x = LID_OPEN
        macbook.updateWorldMatrix(true, true)
        lid.attach(screenAnchor)
        lid.rotation.x = 0
        lidLoaded = true
        tryStartLidAnim()
    }

    // ── Controles GUI do MacBook ───────────────────────────────
    const f = gui.addFolder('MacBook')
    f.add(macbook.position, 'x', -10, 10, 0.01).name('pos X')
    f.add(macbook.position, 'y', -10, 10, 0.01).name('pos Y')
    f.add(macbook.position, 'z', -10, 10, 0.01).name('pos Z')
    f.add(macbook.rotation, 'x', -Math.PI, Math.PI, 0.01).name('rot X')
    f.add(macbook.rotation, 'y', -Math.PI, Math.PI, 0.01).name('rot Y')
    f.add(macbook.rotation, 'z', -Math.PI, Math.PI, 0.01).name('rot Z')
    f.add(macbook.scale, 'x', 0.001, 5, 0.001).name('scale').onChange(v => macbook.scale.set(v, v, v))
    if (lid) f.add(lid.rotation, 'x', -Math.PI, Math.PI, 0.01).name('tampa (rot X)')
    f.close()
})

// ─────────────────────────────────────────────────────────────
// MODELO: ILHA
// ─────────────────────────────────────────────────────────────
let islandMesh = null

// mobile: mesma ilha simplificada (65k tris, texturas 1024 — tools/optimize-island.mjs)
gltfLoader.load(IS_MOBILE ? '/models/island-mobile.glb' : '/models/island.glb', (gltf) =>
{
    islandMesh = gltf.scene

    // Centraliza o modelo: calcula a bounding box e subtrai o centro da posição
    // Sem isso, o pivot do modelo pode ficar deslocado para um lado
    const box = new THREE.Box3().setFromObject(islandMesh)
    const center = box.getCenter(new THREE.Vector3())
    islandMesh.position.sub(center)

    islandMesh.rotation.x = 0.1
    islandMesh.rotation.y = 3.14
    islandMesh.rotation.z = 0

    // receiveShadow = true: esse mesh recebe sombras projetadas por outros objetos
    // + BVH: raycast nos ~106k triângulos da ilha em <0.1ms (hover/drag)
    islandMesh.traverse((child) => {
        if (child.isMesh) {
            child.receiveShadow = true
            child.geometry.computeBoundsTree()
        }
    })

    islandGroup.add(islandMesh)
})

// ─────────────────────────────────────────────────────────────
// LUZES
// Três luzes direcionais + hemisphere criam um setup estilo estúdio:
// Key light = luz principal (quente, diagonal)
// Fill light = luz de preenchimento (fria, lado oposto)
// Rim light  = luz de contorno (de trás, separa o objeto do fundo)
// Hemisphere = luz ambiente com gradiente céu/chão
// ─────────────────────────────────────────────────────────────

// HemisphereLight(corCéu, corChão, intensidade)
// Ilumina de cima com uma cor e de baixo com outra — imita luz ambiente natural
const hemisphereLight = new THREE.HemisphereLight('#b4d4ff', '#ffe4b5', 1.2)
scene.add(hemisphereLight)

// Luz principal — vem de cima/direita, levemente quente
const keyLight = new THREE.DirectionalLight('#fff5e0', 3.5)
keyLight.position.set(2, 3, 1)
keyLight.castShadow = true              // essa luz projeta sombras
keyLight.shadow.mapSize.setScalar(IS_MOBILE ? 512 : 1024) // resolução do mapa de sombra (maior = mais nítido)
keyLight.shadow.camera.near = 0.1       // distância mínima para calcular sombra
keyLight.shadow.camera.far  = 20        // distância máxima
keyLight.shadow.camera.left   = -3      // limites da "câmera" da sombra (frustum)
keyLight.shadow.camera.right  =  3
keyLight.shadow.camera.top    =  3
keyLight.shadow.camera.bottom = -3
// bias/normalBias: sem eles o Mac (que projeta E recebe sombra) ganha "shadow acne"
// — as listras finas que aparecem na superfície. Ajustáveis no ?debug (Key Light).
keyLight.shadow.bias       = -0.0005
keyLight.shadow.normalBias =  0.02
scene.add(keyLight)

// Luz de preenchimento — vem da esquerda, azulada, suaviza as sombras duras
const fillLight = new THREE.DirectionalLight('#c8d8ff', 1.2)
fillLight.position.set(-2, 0.5, 2)
scene.add(fillLight)

// Rim light — vem de trás/baixo, destaca o contorno do modelo
const rimLight = new THREE.DirectionalLight('#ffffff', 1.0)
rimLight.position.set(0, -1, -3)
scene.add(rimLight)

// ── GUI das luzes ──────────────────────────────────────────────
const lightFolder = gui.addFolder('Luzes')

// Botão para ligar/desligar todas as luzes de uma vez
const lightsToggle = { ligadas: true }
lightFolder.add(lightsToggle, 'ligadas').name('on / off').onChange(v =>
{
    hemisphereLight.visible = v
    keyLight.visible        = v
    fillLight.visible       = v
    rimLight.visible        = v
})

const hemiFolder = lightFolder.addFolder('Hemisphere')
const hemiParams = { sky: '#b4d4ff', ground: '#ffe4b5' }
hemiFolder.addColor(hemiParams, 'sky').name('céu').onChange(v => hemisphereLight.color.set(v))
hemiFolder.addColor(hemiParams, 'ground').name('chão').onChange(v => hemisphereLight.groundColor.set(v))
hemiFolder.add(hemisphereLight, 'intensity', 0, 5, 0.05).name('intensidade')

const keyFolder = lightFolder.addFolder('Key Light')
const keyParams = { color: '#fff5e0' }
keyFolder.addColor(keyParams, 'color').name('cor').onChange(v => keyLight.color.set(v))
keyFolder.add(keyLight, 'intensity', 0, 10, 0.1).name('intensidade')
keyFolder.add(keyLight.position, 'x', -10, 10, 0.1).name('pos X')
keyFolder.add(keyLight.position, 'y', -10, 10, 0.1).name('pos Y')
keyFolder.add(keyLight.position, 'z', -10, 10, 0.1).name('pos Z')
keyFolder.add(keyLight.shadow, 'bias',       -0.005, 0.005, 0.0001).name('shadow bias')
keyFolder.add(keyLight.shadow, 'normalBias',  0,     0.1,   0.001 ).name('shadow normalBias')

const fillFolder = lightFolder.addFolder('Fill Light')
const fillParams = { color: '#c8d8ff' }
fillFolder.addColor(fillParams, 'color').name('cor').onChange(v => fillLight.color.set(v))
fillFolder.add(fillLight, 'intensity', 0, 10, 0.1).name('intensidade')
fillFolder.add(fillLight.position, 'x', -10, 10, 0.1).name('pos X')
fillFolder.add(fillLight.position, 'y', -10, 10, 0.1).name('pos Y')
fillFolder.add(fillLight.position, 'z', -10, 10, 0.1).name('pos Z')

const rimFolder = lightFolder.addFolder('Rim Light')
const rimParams = { color: '#ffffff' }
rimFolder.addColor(rimParams, 'color').name('cor').onChange(v => rimLight.color.set(v))
rimFolder.add(rimLight, 'intensity', 0, 10, 0.1).name('intensidade')
rimFolder.add(rimLight.position, 'x', -10, 10, 0.1).name('pos X')
rimFolder.add(rimLight.position, 'y', -10, 10, 0.1).name('pos Y')
rimFolder.add(rimLight.position, 'z', -10, 10, 0.1).name('pos Z')
hemiFolder.close()
keyFolder.close()
fillFolder.close()
rimFolder.close()
lightFolder.close()

// ─────────────────────────────────────────────────────────────
// TAMANHO DA JANELA
// Guardamos width/height para usar em câmera, renderer e scroll.
// O evento 'resize' atualiza tudo quando o usuário redimensiona a janela.
// ─────────────────────────────────────────────────────────────
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Celular: a barra do navegador aparece/some no scroll e muda só a ALTURA → ignora
    // (redimensionar o canvas a cada vez engasga). Girar o aparelho muda a largura → segue.
    if (IS_MOBILE && window.innerWidth === sizes.width) return

    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Atualiza o aspect ratio da câmera (largura ÷ altura)
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix() // obrigatório após mudar propriedades da câmera

    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO))
    fitIslandMobile()
})

// ─────────────────────────────────────────────────────────────
// CÂMERA
// PerspectiveCamera(fov, aspect, near, far)
//   fov   = campo de visão em graus (35 = lente um pouco fechada)
//   near  = objetos mais próximos que 0.5 não aparecem
//   far   = objetos mais longe que 30 não aparecem
// near/far justos (a cena fica a ~6 da câmera) = depth buffer bem mais preciso
// → dispensa o logarithmicDepthBuffer (que desligava o early-Z e pesava no GPU).
// ─────────────────────────────────────────────────────────────
const cameraGroup = new THREE.Group()
scene.add(cameraGroup)

const camera = new THREE.PerspectiveCamera(35, sizes.width / sizes.height, 0.5, 30)
camera.position.z = 6 // câmera recuada 6 unidades para frente da cena
cameraGroup.add(camera)

// A câmera desce com o scroll: objectsDistance unidades 3D por altura de tela (a altura
// visível em z=0 é ~3.78 → a ilha sobe junto com a página, um tiquinho mais rápido).
const objectsDistance = 4

// Enquadramento no celular (retrato): a mesma câmera do desktop enxerga ~1/4 da
// largura → a ilha sairia cortada. Encolhe a ilha até caber (na largura e na faixa
// livre entre o nav e o topo do MOTION, que fica inteiro no pé da 1ª tela) e a centraliza
// nessa faixa — o peso fica equilibrado entre o nav e o MOTION. Escala na ilha (e não câmera
// mais longe) mantém a perspectiva do desktop e a velocidade da ilha no scroll
// (objectsDistance) igual à da página.
// Quem aplica escala/posição é o tick() (applyMobileHero), a partir do mobileHero.
// Valores de design (motionGap, raise, size, shadow e o --motion-h do CSS) = os escolhidos pelo
// Mateus no painel "Ajuste" em 30/09/2026.
const MOBILE_FIT = {
    width:   3.0,    // largura da ilha+Mac em unidades 3D (medida: 2.77 + margem de perspectiva/laterais)
    height:  2.15,   // altura da caixa da ilha+Mac (a pedra+Mac medem 2.0 = `solid`; o resto é folga)
    solid:   2.0,
    centerY: 0.27,   // centro da ilha+Mac no islandGroup (o Mac em cima sobe o centro)
    bandGap: 12,     // margem da faixa livre acima do MOTION (= a de baixo do nav → ilha centrada)
    raise:   13,     // px que a ilha sobe a partir do centro da faixa livre — ?debug
    size:    0.99,   // multiplica o tamanho que cabe (1 = encosta nas laterais; >1 corta) — ?debug
    motionGap: -32,  // px do topo do MOTION até a base da pedra (− = o MOTION começa ATRÁS da ilha,
                     // como no desktop) — o MOTION e as seções de baixo acompanham a ilha — ?debug
    shadow:  1.05,   // força da sombra no MOTION (multiplica a opacidade) — ?debug
}
// Estado do hero no mobile — animado pela coreografia (ver "MOBILE — coreografia") e
// aplicado a cada frame no tick(). y = CENTRO da ilha em fração da altura da tela
// (0 topo → 1 base). Durante o loading a ilha fica em repouso (escondida pelo loader) →
// shaders e texturas compilam antes do corte.
const mobileHero = {
    y:      0.5,                // centro da ilha → fitCache.restY (repouso)
    scale:  1,                  // multiplicador da escala de enquadramento
    float:  0,                  // 0 → 1 liga a flutuação ociosa
    power:  IS_MOBILE ? 0 : 1,  // tela do Mac: 0 apagada → 1 acesa (desktop: sempre 1)
    shadow: 0,                  // sombra no MOTION: 0 → 1 conforme a ilha entra
}
// restY = centro da ilha em repouso; islandH = altura da caixa da ilha em fração da tela
const fitCache = { halfH: 1, scale: 1, restY: 0.5, islandH: 0.4 }
let mobileIntroFinished = false
function fitIslandMobile() {
    if (!IS_MOBILE) return
    const halfH = camera.position.z * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) // meia altura visível em z=0
    const pxPerUnit = sizes.height / (2 * halfH)
    // Faixa livre (px, medida no documento): do pé do nav até o topo que o MOTION teria alinhado
    // à base da tela — a referência de equilíbrio (o --motion-lift, que esta função calcula, fica
    // de fora). Mede o wrapper (.hero-motion), nunca a faixa: o rect dela inclui a transform da câmera.
    const corpoEl = heroMotionEl?.parentElement
    const navBottom = document.querySelector('nav')?.getBoundingClientRect().bottom ?? 60
    const motion = heroMotionEl?.getBoundingClientRect()
    const lift = corpoEl ? parseFloat(getComputedStyle(corpoEl).getPropertyValue('--motion-lift')) || 0 : 0
    const baseTop = motion ? motion.top + window.scrollY + lift : sizes.height * 0.75
    const bandTop = navBottom + 12
    const bandBottom = Math.min(baseTop - MOBILE_FIT.bandGap, sizes.height)
    const band = Math.max(bandBottom - bandTop, sizes.height * 0.3)
    fitCache.halfH = halfH
    fitCache.scale = Math.min(1,
        (2 * halfH * camera.aspect) / MOBILE_FIT.width,   // cabe na largura
        band / pxPerUnit / MOBILE_FIT.height)              // cabe na faixa livre
        * MOBILE_FIT.size
    // repouso: centro da faixa livre, `raise` px acima
    const islandPx = MOBILE_FIT.height * fitCache.scale * pxPerUnit
    const centerPx = bandTop + band / 2 - MOBILE_FIT.raise
    fitCache.restY = centerPx / sizes.height
    fitCache.islandH = islandPx / sizes.height
    // O MOTION (e tudo abaixo dele) acompanha a ilha: o topo dele fica `motionGap` px da base da
    // pedra (negativo = começa atrás dela). Vale em qualquer altura de tela — é a relação que o
    // Mateus desenhou, não uma distância da base.
    if (corpoEl) {
        const rockBottom = centerPx + islandPx * (MOBILE_FIT.solid / MOBILE_FIT.height) / 2
        corpoEl.style.setProperty('--motion-lift', `${Math.round(baseTop - (rockBottom + MOBILE_FIT.motionGap))}px`)
    }
    // repouso: sob o loader (aquecimento) e em resize depois da intro
    if (mobileIntroFinished || !siteIntroStarted) mobileHero.y = fitCache.restY
}
fitIslandMobile()

// Aplica o mobileHero na ilha — chamado pelo tick() a cada frame (só no mobile).
const heroShadowEl = IS_MOBILE ? document.querySelector('.hero-shadow') : null
const shadowLast = { o: -1, k: -1 }
function applyMobileHero(t) {
    const s = fitCache.scale * mobileHero.scale
    const wave = Math.sin(t * 1.1) * mobileHero.float           // −1…1: flutuação ociosa ("ilha boiando")
    mobileFit.scale.setScalar(s)
    mobileFit.position.y = fitCache.halfH * (1 - 2 * mobileHero.y) + (wave * 0.03 - MOBILE_FIT.centerY) * s
    islandGroup.rotation.z = Math.sin(t * 0.8) * 0.012 * mobileHero.float
    // tela: power² ≈ brilho percebido linear (o fade não "estoura" no fim)
    if (screenMaterial) screenMaterial.color.setScalar(mobileHero.power * mobileHero.power)
    // Sombra no MOTION: aparece com a ilha, respira com a flutuação (ilha mais alta → sombra
    // mais clara e aberta) e some quando a ilha sai rolando. Só escreve o que mudou.
    if (heroShadowEl) {
        const away = Math.min(scrollY / (sizes.height * 0.45), 1)
        const o = Math.round(Math.min(1, mobileHero.shadow * MOBILE_FIT.shadow * (1 - 0.14 * wave)) * (1 - away) * 100) / 100
        const k = Math.round((0.86 + 0.14 * mobileHero.shadow + 0.035 * wave) * 1000) / 1000
        if (o !== shadowLast.o) { heroShadowEl.style.opacity = o; shadowLast.o = o }
        if (k !== shadowLast.k) { heroShadowEl.style.transform = `scale(${k})`; shadowLast.k = k }
    }
}

// ─────────────────────────────────────────────────────────────
// RENDERER
// O renderer pega a cena + câmera e desenha tudo no canvas.
//   alpha: true              → fundo transparente (mostra o CSS por baixo)
//   antialias: true          → suaviza bordas serrilhadas
//   pixel ratio máx 1.5      → em tela retina (DPR 2) o canvas fullscreen tinha 4x os
//                              pixels de 1x; 1.5 fica nítido e custa ~44% menos no GPU
//                              (mobile: 1.25 — celular tem DPR 3 e GPU/bateria menores)
//   outputColorSpace SRGB    → cores corretas para displays modernos
//   ACESFilmicToneMapping    → algoritmo cinematográfico de mapeamento de cores
//   PCFSoftShadowMap         → sombras com bordas suavizadas
// ─────────────────────────────────────────────────────────────
const MAX_PIXEL_RATIO = IS_MOBILE ? 1.25 : 1.5
const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    alpha: true,
    antialias: true,
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO))
renderer.outputColorSpace = THREE.SRGBColorSpace
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1.0
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap

// ─────────────────────────────────────────────────────────────
// TELA DO MACBOOK — <video> → VideoTexture aplicada no Object_7 (ver o load do Mac)
// O <video> não entra no DOM; só alimenta a textura.
// ─────────────────────────────────────────────────────────────
// (sem autoplay: todo play é explícito via playVideo — ver "Autoplay no iPhone" no CLAUDE.md;
//  com autoplay o Chrome ainda ignoraria o preload='metadata' do mobile)
const screenVideoEl = document.createElement('video')
screenVideoEl.loop = true
screenVideoEl.muted = true
screenVideoEl.playsInline = true
screenVideoEl.src = '/video/motion-screen-640.mp4'
if (IS_MOBILE) {
    // Mobile: a tela só "acende" no meio da coreografia → não baixa nem toca durante o
    // loading (aí o único vídeo tocando é o do loader; menos disputa de decoder/banda).
    // Começa aos MOBILE_INTRO.screenAt (longe do frame do corte) — ver playMobileIntro.
    screenVideoEl.preload = 'metadata'
} else {
    screenVideoEl.preload = 'auto'   // baixa já durante o loading
    // sinaliza ao loader que a tela está pronta (ou falhou) → libera o reveal
    screenVideoEl.addEventListener('canplay', () => { screenReady = true; tryReveal() }, { once: true })
    screenVideoEl.addEventListener('error',   () => { screenReady = true; tryReveal() }, { once: true })
    playVideo(screenVideoEl)
}

const screenTexture = new THREE.VideoTexture(screenVideoEl)
screenTexture.colorSpace = THREE.SRGBColorSpace
// flipY padrão (true) de propósito: no quad Object_7 o TOPO da tela tem v≈1 (UV no
// estilo Blender, não glTF). Com flipY=false o vídeo aparece de cabeça pra baixo.
// "object-fit: cover": o vídeo (16:9) preenche a tela (16:10) pela altura e corta
// as laterais — mesmo enquadramento de antes.
const SCREEN_ASPECT = 16 / 10
screenVideoEl.addEventListener('loadedmetadata', () => {
    const videoAspect = screenVideoEl.videoWidth / screenVideoEl.videoHeight
    const visibleX = Math.min(1, SCREEN_ASPECT / videoAspect)
    screenTexture.repeat.set(visibleX, 1)
    screenTexture.offset.set((1 - visibleX) / 2, 0)
})


// ── Luz da tela (screen glow) ───────────────────────────────
// Canvas minúsculo p/ amostrar a cor média do vídeo. Ler pixels do vídeo força o
// GPU a devolver o frame p/ o CPU (caro) — por isso só ~10x/s, num canvas 16×16,
// e a cor da luz interpola entre as amostras (ver tick).
const SAMPLE_SIZE = 16
const SAMPLE_INTERVAL = IS_MOBILE ? 0.25 : 0.1   // s (mobile: ~4x/s — CPU do celular)
const sampleCanvas = document.createElement('canvas')
sampleCanvas.width = SAMPLE_SIZE
sampleCanvas.height = SAMPLE_SIZE
const sampleCtx = sampleCanvas.getContext('2d', { willReadFrequently: true })
const screenGlowTarget = { color: new THREE.Color(0, 0, 0), intensity: 0 }
let lastSampleTime = -1

// PointLight que muda de cor dinamicamente conforme o vídeo.
// decay: 1 = queda linear (mais difusa) | decay: 2 = queda quadrática (muito concentrada)
const screenLight = new THREE.PointLight('#ffffff', 0, 6, 1)
scene.add(screenLight)


// PMREMGenerator cria um environment map a partir do RoomEnvironment (estúdio virtual).
// scene.environment aplica esse mapa como reflexo em todos os materiais PBR da cena.
const pmremGenerator = new THREE.PMREMGenerator(renderer)
scene.environment = pmremGenerator.fromScene(new RoomEnvironment(), 0.04).texture
pmremGenerator.dispose() // libera memória — o mapa já foi gerado e salvo

// ─────────────────────────────────────────────────────────────
// SCROLL — Lenis (suavização)
// ─────────────────────────────────────────────────────────────
let scrollY      = 0   // scroll do Lenis (atualizado no evento 'scroll' dele)
let lastScrollAt = 0   // performance.now() do último scroll (mobile: render ocioso a 30fps)

// Cubic bezier via Newton-Raphson — igual ao que os browsers fazem internamente.
// Permite replicar qualquer easing do After Effects com x1,y1,x2,y2.
function makeCubicBezier(x1, y1, x2, y2) {
    const sample = (t, a, b) => 3*(1-t)*(1-t)*t*a + 3*(1-t)*t*t*b + t*t*t
    const deriv  = (t)       => 3*(1-t)*(1-t)*x1 + 6*(1-t)*t*(x2-x1) + 3*t*t*(1-x2)
    return (x) => {
        if (x <= 0) return 0
        if (x >= 1) return 1
        // Newton-Raphson com bisseção de segurança: mantém t dentro de [lo,hi] ⊂ [0,1].
        // Sem isso, curvas degeneradas (ex.: 0,0,0,1 → X(t)=t³, quase plana no início)
        // fariam o Newton divergir e devolver progresso negativo/>1 (overshoot visível).
        let t = x, lo = 0, hi = 1
        for (let i = 0; i < 12; i++) {
            const xt = sample(t, x1, x2)
            if (Math.abs(xt - x) < 1e-6) break
            if (xt < x) lo = t; else hi = t
            const d  = deriv(t)
            const nt = t - (xt - x) / d
            t = (Math.abs(d) < 1e-6 || !isFinite(nt) || nt <= lo || nt >= hi) ? (lo + hi) / 2 : nt
        }
        return sample(t, y1, y2)
    }
}

// ─────────────────────────────────────────────────────────────
// TAMPA — easing e GUI
// ─────────────────────────────────────────────────────────────
const lidParams = {
    influSaida:  7.4, // 0–10: velocidade de saída (0=devagar, 10=rápido)
    influEntrada: 2.7, // 0–10: suavidade de entrada (0=brusco, 10=muito suave)
    duration: 1.8,
    delay:    0,
}

function makeLidEase() {
    const s = lidParams.influSaida   / 10  // 0–1
    const e = lidParams.influEntrada / 10  // 0–1
    const x1 = Math.max(0.01, 1 - s * 0.95) // saída alta → x1 pequeno
    const y1 = s                             // saída alta → y1 alto (velocidade inicial)
    const x2 = Math.min(0.99, 0.3 + e * 0.65) // entrada alta → x2 grande (desacelera cedo)
    const y2 = 1
    return makeCubicBezier(x1, y1, x2, y2)
}

function replayLidAnim(useDelay = true) {
    if (!lid) return
    gsap.killTweensOf(lid.rotation)
    lid.rotation.x = 0
    gsap.to(lid.rotation, {
        x: LID_OPEN,
        duration: lidParams.duration,
        ease: makeLidEase(),
        delay: useDelay ? lidParams.delay : 0,
    })
}

const lidFolder = gui.addFolder('Tampa (animação)')
lidFolder.add(lidParams, 'influSaida',   0, 10, 0.1).name('influência saída').onChange(() => replayLidAnim(false))
lidFolder.add(lidParams, 'influEntrada', 0, 10, 0.1).name('influência entrada').onChange(() => replayLidAnim(false))
lidFolder.add(lidParams, 'duration', 0.3, 3.0, 0.05).name('duração (s)')
lidFolder.add(lidParams, 'delay',    0.0, 2.0, 0.05).name('delay (s)')
lidFolder.add({ replay: () => replayLidAnim(false) }, 'replay').name('↺ repetir')
lidFolder.close()

const lenis = new Lenis({
    duration:    1.2,
    easing:      t => 1 - Math.pow(1 - t, 3),
    smoothWheel: true,
    // No toque o scroll passa pelo pipeline do Lenis: o evento 'scroll' mantém o ScrollTrigger
    // em dia, o lenis.stop() segura a página durante o drag da ilha e a entrada da coreografia,
    // e o arrasto sobre os canvas do Rive (que bloqueiam o toque nativo) continua rolando.
    syncTouch:   true,
    // Ao soltar o dedo a página desliza |velocidade|^expoente px (padrão 1.7 = curto, com
    // "atrito": o Mateus achou pesado). 1.8 ≈ +35–45% de deslize nas rolagens comuns.
    // ?debug no mobile: "scroll: deslize" / "scroll: suavidade".
    touchInertiaExponent: 1.8,
})

// Scroll travado enquanto o loader está na tela (senão a página rolava por trás dele e o
// site aparecia fora do hero) e, no mobile, durante a entrada da ilha. Flag própria: o modal
// de contato e o drag da ilha também param o Lenis.
let scrollHeld = false
function holdScroll() {
    if (scrollHeld) return
    scrollHeld = true
    lenis.stop()
}
function releaseScroll() {
    if (!scrollHeld) return
    scrollHeld = false
    // modal de contato aberto → ele mesmo religa o Lenis ao fechar
    if (!contactModal?.classList.contains('is-open')) lenis.start()
}

// Sempre começa no hero: zera o scroll interno do Lenis no init e no load — este só com o
// loader na tela (numa rede lenta o user já pode estar rolando quando o load chega).
// A restauração nativa já foi desligada no topo do módulo.
lenis.scrollTo(0, { immediate: true })
holdScroll()
window.addEventListener('load', () => { if (!siteCut) lenis.scrollTo(0, { immediate: true, force: true }) })

lenis.on('scroll', ({ scroll }) => {
    scrollY = scroll
    lastScrollAt = performance.now()
    ScrollTrigger.update()
    if (prevMouseX !== null) hoverDirty = true   // a ilha se move sob o mouse parado
})

// Conecta Lenis ao ticker do GSAP — ambos rodam no mesmo rAF
gsap.ticker.add(time => lenis.raf(time * 1000))
gsap.ticker.lagSmoothing(0)

// ─────────────────────────────────────────────────────────────
// NAV — scroll suave ao clicar nos links (#services / #work / #contact)
// Easing: cubic-bezier .8,0,.2,1 (ease-in-out: começa devagar, acelera no
// meio, desacelera no fim). Em #services/#work a "linha" (border-top da
// section) é o ponto de referência: ela para ~80px abaixo do nav.
// Em #contact rola até o fim da página (mostra o footer/wordmark inteiro).
// ─────────────────────────────────────────────────────────────
const navEase = makeCubicBezier(0.8, 0, 0.2, 1)
const NAV_GAP = 80   // px entre a base do nav e a linha da section

document.querySelectorAll('a[href^="#"]').forEach((link) => {
    link.addEventListener('click', (e) => {
        const hash = link.getAttribute('href')
        if (!hash || hash === '#') {
            // logo do nav / "Home" do footer → topo
            e.preventDefault()
            lenis.scrollTo(0, { duration: 1.2, easing: navEase })
            return
        }
        const target = document.querySelector(hash)
        if (!target) return
        e.preventDefault()

        const opts = { duration: 1.2, easing: navEase, onComplete: () => ScrollTrigger.update() }

        if (hash === '#contact') {
            // contato → fim da página (sem folga no topo)
            const maxScroll = document.documentElement.scrollHeight - window.innerHeight
            lenis.scrollTo(maxScroll, opts)
        } else {
            // services/work → a linha da section para ~80px abaixo do nav
            const navEl  = document.querySelector('nav')
            const navBot = navEl ? navEl.getBoundingClientRect().bottom : 0
            lenis.scrollTo(target, { ...opts, offset: -(navBot + NAV_GAP) })
        }
    })
})

// ─────────────────────────────────────────────────────────────
// RAYCAST — a ilha está sob o ponteiro? (hover, drag)
// ─────────────────────────────────────────────────────────────
const raycaster = new THREE.Raycaster()
raycaster.firstHitOnly = true   // (BVH) só precisamos saber SE acertou a ilha
const mouseNDC = new THREE.Vector2()
const pointerNDC = new THREE.Vector2()

// true se o ponto (NDC) cai em cima da ilha ou do Mac
function hitsIsland(ndc) {
    raycaster.setFromCamera(ndc, camera)
    return raycaster.intersectObject(islandGroup, true).length > 0
}

// ─────────────────────────────────────────────────────────────
// CURSOR — CSS nativo; browser renderiza no nível do OS
// JS só troca classe no <html>; hotspots definidos no CSS
// ─────────────────────────────────────────────────────────────
let activeCursor = 'default'
let isOverClickable = false
let isOverRivePointer = false // ← ligado pelo boolean "pointerOn" de rives (ex: header)

function setCursorType(name) {
    if (activeCursor === name) return
    document.documentElement.classList.remove('cursor-open', 'cursor-closed', 'cursor-pointing')
    if (name !== 'default') document.documentElement.classList.add(`cursor-${name}`)
    activeCursor = name
}

document.querySelectorAll('a, button, [role="button"]').forEach(el => {
    el.addEventListener('mouseenter', () => { isOverClickable = true;  setCursorType('pointing') })
    el.addEventListener('mouseleave', () => { isOverClickable = false; setCursorType('default')  })
})

// Bloqueia o menu nativo do botão direito — menos em links e campos (copiar o e-mail,
// colar no form de contato)
window.addEventListener('contextmenu', (e) => {
    if (e.target instanceof Element && e.target.closest('a, input, textarea')) return
    e.preventDefault()
})

// Velocidade suavizada do mouse para o hover-push
let hoverVelX = 0, hoverVelY = 0
let prevMouseX = null, prevMouseY = null
// O raycast do hover roda no tick() (no máx. 1x por frame), não a cada mousemove —
// mouses de 120Hz+ disparavam vários raycasts por frame. O "empurrão" de cada
// evento é acumulado aqui e aplicado no tick se o mouse estiver sobre a ilha.
let hoverDirty = false
let hoverPushX = 0, hoverPushY = 0
let hoverOverUI = false

// Elementos de UI: nunca iniciam o drag nem recebem o hover da ilha
// (ex.: com o modal de contato aberto por cima do hero)
const UI_TARGETS = 'a, button, input, textarea, select, label, nav, [role="button"], .contact-modal'

window.addEventListener('mousemove', (event) =>
{
    const rawVX = prevMouseX !== null ? event.clientX - prevMouseX : 0
    const rawVY = prevMouseY !== null ? event.clientY - prevMouseY : 0
    prevMouseX = event.clientX
    prevMouseY = event.clientY
    // posição sempre atualizada (inclusive no drag) — o endDrag reavalia o hover
    // no ponto onde o mouse FOI SOLTO, não onde o drag começou
    mouseNDC.x =  (event.clientX / sizes.width)  * 2 - 1
    mouseNDC.y = -(event.clientY / sizes.height) * 2 + 1
    // sobre UI (modal de contato, nav, botões…) a ilha não reage ao hover
    hoverOverUI = event.target instanceof Element && !!event.target.closest(UI_TARGETS)

    if (rotState.dragging) {
        hoverVelX = 0
        hoverVelY = 0
        return
    }

    hoverVelX = hoverVelX * 0.5 + rawVX * 0.5
    hoverVelY = hoverVelY * 0.5 + rawVY * 0.5
    hoverPushX += hoverVelX
    hoverPushY += hoverVelY
    hoverDirty = true
})

// Chamado pelo tick() quando o mouse mexeu (ou a página rolou): cursor "mão aberta"
// sobre a ilha + leve empurrão na direção do movimento.
function updateHover() {
    const pushX = hoverPushX, pushY = hoverPushY
    hoverPushX = hoverPushY = 0
    // no celular não há hover (os mousemove "de compatibilidade" após um toque não
    // devem empurrar a ilha nem gerar raycast a cada frame de scroll)
    if (IS_MOBILE) return
    if (rotState.dragging || isOverClickable || isOverRivePointer) return
    if (hoverOverUI) { if (activeCursor === 'open') setCursorType('default'); return }
    if (!interactionReady) { setCursorType('default'); return } // durante o loading, só cursor default

    const hit = hitsIsland(mouseNDC)
    setCursorType(hit ? 'open' : 'default')

    if (hit) {
        const limitRad = rotParams.limitDeg * Math.PI / 180
        const str = 0.00025
        rotState.targetY = Math.max(-limitRad, Math.min(limitRad, rotState.targetY + pushX * str))
        rotState.targetX = Math.max(-limitRad, Math.min(limitRad, rotState.targetX + pushY * str))
    }
}


// ─────────────────────────────────────────────────────────────
// ROTAÇÃO COM DRAG + RETORNO ELÁSTICO
// ─────────────────────────────────────────────────────────────
const baseGroupRot = { x: 0.2, y: -0.07 }

// Intro: ilha começa rotacionada — animação disparada por startSiteIntro()
// (mobile: 0 — repousa na pose default sob o loader; o giro inicial é da coreografia)
const introOffset = { y: IS_MOBILE ? 0 : 0.7 }

const rotParams = {
    sensitivity: 0.005,
    limitDeg:    30,
    dragLerp:    0.12,
    returnLerp:  0.04,
}
const rotState = {
    dragging: false, prevX: 0, prevY: 0,
    targetX: 0, targetY: 0, currentX: 0, currentY: 0,
}

// Toque (celular/tablet): o dedo que começa EM CIMA da ilha segura a ilha — gira em X e Y
// como no desktop, e o Lenis para durante o gesto (com syncTouch ele cancela o touchmove →
// a página não rola). Dedo fora da ilha rola a página normal.
let dragStoppedLenis = false

window.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || !e.isPrimary) return   // 2º dedo (pinça) não mexe na ilha
    if (!interactionReady) return   // sem drag do 3D enquanto o loader está na tela
    if (scrollHeld) return          // mobile: a ilha ainda está entrando (scroll travado)
    if (scrollY > window.innerHeight * 0.5) return
    if (e.target instanceof Element && e.target.closest(UI_TARGETS)) return
    // Só arrasta se o ponteiro está EM CIMA da ilha/Mac (antes: qualquer ponto do hero
    // girava a ilha — inclusive ao selecionar texto).
    pointerNDC.set((e.clientX / sizes.width) * 2 - 1, -(e.clientY / sizes.height) * 2 + 1)
    if (!hitsIsland(pointerNDC)) return
    rotState.prevX = e.clientX
    rotState.prevY = e.clientY
    rotState.dragging = true
    if (e.pointerType === 'touch') {
        if (!lenis.isStopped) { lenis.stop(); dragStoppedLenis = true }
        return
    }
    document.documentElement.classList.add('is-dragging')   // sem seleção de texto no drag
    setCursorType('closed')
})
window.addEventListener('pointermove', (e) => {
    if (!rotState.dragging || !e.isPrimary) return
    const dx = e.clientX - rotState.prevX
    const dy = e.clientY - rotState.prevY
    rotState.prevX = e.clientX
    rotState.prevY = e.clientY
    const limitRad = rotParams.limitDeg * Math.PI / 180
    rotState.targetY = Math.max(-limitRad, Math.min(limitRad, rotState.targetY + dx * rotParams.sensitivity))
    rotState.targetX = Math.max(-limitRad, Math.min(limitRad, rotState.targetX + dy * rotParams.sensitivity))
})
const endDrag = (e) => {
    if (!e.isPrimary) return
    // só religa o Lenis se foi o drag que parou (o modal de contato e a coreografia também o param)
    if (dragStoppedLenis) { dragStoppedLenis = false; lenis.start() }
    if (!rotState.dragging) return
    rotState.dragging = false
    document.documentElement.classList.remove('is-dragging')
    setCursorType((isOverClickable || isOverRivePointer) ? 'pointing' : 'default')
    hoverDirty = true   // o próximo tick reavalia: ainda sobre a ilha → mão aberta
}
window.addEventListener('pointerup',     endDrag)
window.addEventListener('pointercancel', endDrag)

const rotFolder = gui.addFolder('Rotação (drag)')
rotFolder.add(rotParams, 'sensitivity', 0.001, 0.02,  0.0001).name('sensibilidade')
rotFolder.add(rotParams, 'limitDeg',    5,     90,    1     ).name('limite (graus)')
rotFolder.add(rotParams, 'dragLerp',    0.01,  1.0,   0.01  ).name('suavidade drag')
rotFolder.add(rotParams, 'returnLerp',  0.005, 0.3,   0.005 ).name('velocidade retorno')
rotFolder.close()

// ─────────────────────────────────────────────────────────────
// ZOOM COM PINCH + RETORNO ELÁSTICO
// ─────────────────────────────────────────────────────────────
const baseCameraZ = 6

const zoomParams = {
    sensitivity:  0.008,
    limit:        2.0,
    lerpSpeed:    0.12,
    returnSpeed:  0.04,
}
const zoomState = { active: false, target: 0, current: 0 }
let zoomTimer = null

const zoomFolder = gui.addFolder('Zoom')
zoomFolder.add(zoomParams, 'sensitivity', 0.001, 0.05,  0.001).name('sensibilidade')
zoomFolder.add(zoomParams, 'limit',       0.5,   5.0,   0.1  ).name('limite')
zoomFolder.add(zoomParams, 'lerpSpeed',   0.01,  1.0,   0.01 ).name('suavidade')
zoomFolder.add(zoomParams, 'returnSpeed', 0.005, 0.3,   0.005).name('velocidade retorno')
zoomFolder.close()

gui.close()

// Zoom via pinch / Ctrl+scroll — interceptado antes do Lenis (capture: true).
// Só com ?debug: no site público o pinch fica com o comportamento normal do browser.
if (DEBUG) window.addEventListener('wheel', (e) => {
    if (!e.ctrlKey) return
    e.preventDefault()
    zoomState.active = true
    zoomState.target = Math.max(-zoomParams.limit, Math.min(zoomParams.limit, zoomState.target + e.deltaY * zoomParams.sensitivity))
    clearTimeout(zoomTimer)
    zoomTimer = setTimeout(() => { zoomState.active = false }, 400)
}, { passive: false, capture: true })

// ─────────────────────────────────────────────────────────────
// LOOP DE ANIMAÇÃO (tick)
// Roda no gsap.ticker (1x por frame, sincronizado com o monitor), DEPOIS das timelines do
// GSAP e do Lenis → a ilha sempre renderiza com o estado daquele frame (o corte do loader,
// a coreografia, o scroll). deltaTime = tempo desde o último frame — as animações ficam
// independentes do framerate (60fps ou 30fps ficam iguais visualmente).
// ─────────────────────────────────────────────────────────────
const clock = new THREE.Clock()
let previousTime = 0

document.addEventListener('visibilitychange', () => {
    if (!document.hidden) previousTime = clock.getElapsedTime()
})

const screenPos = new THREE.Vector3()
let wasOffscreen = null            // null → aplica a visibilidade no 1º frame
let screenPausedOffscreen = false

// Mobile ocioso: coreografia terminada (e nenhuma timeline rodando — ex.: a ilha que chega
// atrasada numa rede lenta), sem scroll nem drag, e a ilha já de volta da rotação elástica.
// Aí a cena quase não muda — flutuação lenta, tela do Mac a 24fps.
let idleSkip = false
function mobileIdle() {
    return mobileIntroFinished && !introTl?.isActive() && !rotState.dragging
        && performance.now() - lastScrollAt > 400
        && Math.abs(rotState.currentX) + Math.abs(rotState.currentY) < 0.002
}

const tick = () =>
{
    // ── Loader (opaco) na tela → nada de 3D, só os frames de aquecimento ──
    if (loaderCovers) {
        if (warmupFrames === 0) return
        warmupFrames--
    }

    // ── Mobile ocioso → 30fps (renderiza 1 frame sim, 1 não) ────
    // Menos GPU = celular mais frio: quente, o iOS baixa o fps sozinho e passa a bloquear
    // o autoplay dos vídeos (aparecia o botão de play no loading).
    if (IS_MOBILE && mobileIdle()) {
        idleSkip = !idleSkip
        if (idleSkip) return
    }

    const elapsedTime = clock.getElapsedTime()
    const deltaTime   = Math.min(elapsedTime - previousTime, 0.1)
    previousTime      = elapsedTime

    // ── Ilha fora da tela → pula TODO o trabalho do 3D ─────────
    // A câmera desce com o scroll; após ~1.5 viewport a ilha já saiu 100% da tela.
    // Aí não renderiza, não amostra o vídeo, não faz raycast e pausa o vídeo da tela
    // — sobra CPU/GPU p/ os Rives e vídeos das sections de baixo. O loop continua,
    // então ao voltar pro topo tudo retoma sem salto.
    // Usa o scroll REAL (não a var do Lenis, que pode estar 0 logo após um refresh
    // com posição restaurada → evita a "tela fantasma" no meio).
    // (mobile: a ilha é menor → some bem antes; 0.9 libera GPU p/ Services/Work)
    const island3DOffscreen = window.scrollY > sizes.height * (IS_MOBILE ? 0.9 : 1.5)
    if (island3DOffscreen !== wasOffscreen) {
        // esconde o canvas: senão o último frame fica "congelado" visível
        canvas.style.visibility = island3DOffscreen ? 'hidden' : 'visible'
        wasOffscreen = island3DOffscreen
    }
    if (island3DOffscreen) {
        if (!screenVideoEl.paused) { screenVideoEl.pause(); screenPausedOffscreen = true }
        if (hoverDirty) {
            hoverDirty = false
            hoverPushX = hoverPushY = 0
            if (activeCursor === 'open') setCursorType('default')
        }
        return
    }
    if (screenPausedOffscreen) { screenPausedOffscreen = false; playVideo(screenVideoEl) }

    // ── Move a câmera com o scroll ─────────────────────────────
    // scrollY é atualizado pelo Lenis (que roda antes, no mesmo tick)
    camera.position.y = -scrollY / sizes.height * objectsDistance

    // ── Mobile: posição/escala/flutuação/tela/sombra vindas da coreografia ──
    if (IS_MOBILE) applyMobileHero(elapsedTime)

    // ── Hover (cursor + empurrão) — no máx. 1 raycast por frame ──
    if (hoverDirty) { hoverDirty = false; updateHover() }

    // ── Rotação drag com retorno elástico ─────────────────────────
    if (!rotState.dragging) {
        rotState.targetX += (0 - rotState.targetX) * rotParams.returnLerp * 60 * deltaTime
        rotState.targetY += (0 - rotState.targetY) * rotParams.returnLerp * 60 * deltaTime
    }
    rotState.currentX += (rotState.targetX - rotState.currentX) * rotParams.dragLerp * 60 * deltaTime
    rotState.currentY += (rotState.targetY - rotState.currentY) * rotParams.dragLerp * 60 * deltaTime
    // mobile: ao rolar para fora do hero a ilha gira/inclina um pouco (0 no desktop)
    const exitSpin = IS_MOBILE && !REDUCED_MOTION ? Math.min(scrollY / sizes.height, 1) : 0
    islandGroup.rotation.x = baseGroupRot.x + rotState.currentX + exitSpin * 0.15
    islandGroup.rotation.y = baseGroupRot.y + rotState.currentY + introOffset.y + exitSpin * 0.5

    // ── Zoom com retorno elástico (só ?debug: pinça / ctrl+scroll) ──
    if (DEBUG) {
        if (!zoomState.active) {
            zoomState.target += (0 - zoomState.target) * zoomParams.returnSpeed * 60 * deltaTime
        }
        zoomState.current += (zoomState.target - zoomState.current) * zoomParams.lerpSpeed * 60 * deltaTime
        camera.position.z = baseCameraZ + zoomState.current
    }

    // ── Luz da tela: segue o Mac e tinge com a cor média do vídeo ──
    if (screenAnchor) {
        screenAnchor.getWorldPosition(screenPos)
        screenLight.position.copy(screenPos)

        // amostra ~10x/s (ver SAMPLE_INTERVAL) — ler pixels do vídeo é caro (e com a tela
        // apagada — mobile, antes de acender — nem precisa)
        if (mobileHero.power > 0 && screenVideoEl.readyState >= 2 && elapsedTime - lastSampleTime >= SAMPLE_INTERVAL) {
            lastSampleTime = elapsedTime
            sampleCtx.drawImage(screenVideoEl, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE)
            const px = sampleCtx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE).data
            let r = 0, g = 0, b = 0
            for (let i = 0; i < px.length; i += 4) { r += px[i]; g += px[i+1]; b += px[i+2] }
            const n = px.length / 4
            const ar = r / n / 255, ag = g / n / 255, ab = b / n / 255
            screenGlowTarget.color.setRGB(ar, ag, ab)
            screenGlowTarget.intensity = (ar * 0.299 + ag * 0.587 + ab * 0.114) * 2
        }
        // interpola até a última amostra → transição suave entre elas
        const k = 1 - Math.exp(-deltaTime * 15)
        screenLight.color.lerp(screenGlowTarget.color, k)
        screenLight.intensity += (screenGlowTarget.intensity * mobileHero.power - screenLight.intensity) * k
    }

    // ── Renderiza o frame ──────────────────────────────────────
    renderer.render(scene, camera)
}


// ─────────────────────────────────────────────────────────────
// RELÓGIOS — hora local de Florianópolis (America/Sao_Paulo), ao vivo: no footer (com
// segundos) e na legenda do hero (mobile; hora e minuto). Cada um só roda com o elemento na
// tela, alinhado ao próprio passo, e só escreve quando o texto muda.
// (O wordmark do footer é um SVG que escala sozinho.)
// ─────────────────────────────────────────────────────────────
function liveClock(el, fmtOpts, stepMs) {
    if (!el) return
    const fmt = new Intl.DateTimeFormat('en-US', { ...fmtOpts, hour12: true, timeZone: 'America/Sao_Paulo' })
    let timer = null
    let last = ''
    const update = () => {
        const text = fmt.format(new Date())
        if (text !== last) { el.textContent = text; last = text }
    }
    // próximo "tique" logo depois da virada do passo (segundo/minuto)
    const schedule = () => {
        timer = setTimeout(() => { update(); schedule() }, stepMs - (Date.now() % stepMs) + 20)
    }
    update()
    new IntersectionObserver(([e]) => {
        clearTimeout(timer)
        timer = null
        if (e.isIntersecting) { update(); schedule() }
    }).observe(el)
}
liveClock(document.getElementById('footer-clock'), { hour: '2-digit', minute: '2-digit', second: '2-digit' }, 1000)
liveClock(document.getElementById('hero-clock'), { hour: 'numeric', minute: '2-digit' }, 60000)

// ─────────────────────────────────────────────────────────────
// MOBILE — coreografia do hero, textos e Services (só ≤768px; o desktop não passa aqui)
// Uma linguagem de movimento só, que conversa com o objeto 3D:
//   • chegada (ARRIVE = 0,0,0,1): tudo que entra em cena começa rápido e pousa longo;
//   • acender: palavras vão do cinza apagado ao branco numa onda, como leitura — o 1º
//     parágrafo e a citação, com o scroll;
//   • dobradiça: labels e itens do Services "abrem como a tampa do Mac" — sobem de uma
//     máscara girando em X, na mesma direção da tampa;
//   • foco: o que está no centro da tela fica nítido; o resto, desfocado e apagado
//     (a ideia do Rive de Services do desktop).
// Hero: o corte do loader é o mesmo do desktop — o loading sobe acelerado e, no pico, as
// letras do MOTION já estão caindo, uma a uma, com blur, e a ilha gira e cresce NO LUGAR
// (sem deslocamento) enquanto o Mac abre. O MOTION fica inteiro no pé da 1ª tela, a ilha
// centrada entre o nav e ele, e a sombra dela nas letras.
// Linha do tempo (s, a partir do corte): MOTION + giro + tampa (0) → vídeo da tela começa,
// apagado (0.7) → scroll destrava (1.4) → tela acende (1.6) → flutuação (2.2) → fim (3.0).
// Ajuste fino: MOBILE_INTRO ou ?debug → folders "Intro mobile" e "Tampa (animação)" (↺ replay).
// MOTION: uma faixa, não um rótulo — letra com 46vw, palavra com ~2,2 telas, cortada pelas
// bordas; rolando, uma "câmera" atravessa a palavra de "MO" até "ION" (initMotionCamera).
// ─────────────────────────────────────────────────────────────
const ARRIVE = makeCubicBezier(0, 0, 0, 1)

// a entrada da ilha do desktop (startSiteIntro): gira e cresce com esta curva
const ISLAND_IN = makeCubicBezier(0.16, 1, 0.3, 1)

// Valores de partida = os do desktop (giro 0.7 rad, cresce ~18%, letras caem 180px com blur 12)
const MOBILE_INTRO = {
    startSpin:    0.7,   // rad no eixo Y até a pose default
    startScale:   0.85,  // cresce até 1 junto com o giro (≈ o 1 → 1.18 do desktop)
    spinDur:      2.0,   // giro + crescimento (curva ISLAND_IN)
    screenAt:     0.7,   // o vídeo da tela começa (apagado) — longe do frame do corte
    unlockAt:     1.4,   // scroll destrava
    wakeAt:       1.6,   powerDur: 0.9,   // a tela acende (a luz dela tinge a ilha)
    floatAt:      2.2,
    endAt:        3.0,   // fim: libera os Rives do Work + prefetch dos .riv
    motionDrop:   180,   // px que cada letra do MOTION cai no corte
    motionBlur:   12,    // px de blur no começo da queda
    captionAt:    0.35,  // a legenda (se já está na 1ª tela) abre em dobradiça
}

// Letras já na pose de partida (em cima, com blur, camadas próprias) sob o loader: o frame do
// corte só muda valores — nada de criar camada/filtro no momento mais sensível.
if (IS_MOBILE && !REDUCED_MOTION) {
    gsap.set(heroLetters, {
        y: -MOBILE_INTRO.motionDrop, filter: `blur(${MOBILE_INTRO.motionBlur}px)`, willChange: 'transform, filter',
    })
}

// dobradiça: linha "fechada" (dentro da máscara, deitada na direção do leitor) → aberta
const HINGE_FROM = { yPercent: 130, rotationX: -70, transformPerspective: 700, transformOrigin: '50% 100%' }
const HINGE_TO   = { yPercent: 0, rotationX: 0 }

let resolveMobileIntroDone
const mobileIntroDone = IS_MOBILE ? new Promise((r) => { resolveMobileIntroDone = r }) : Promise.resolve()
let introTl       = null    // timeline da coreografia (?debug: window.__intro)
let mobileAwait3D = false   // rede lenta: o fallback revelou o site antes da ilha chegar

// "Acender": as palavras vão do cinza apagado ao branco numa onda (stagger), como uma
// leitura, guiadas pelo scroll (scrub — o Lenis já suaviza). Mesma receita p/ o 1º
// parágrafo e a citação. As palavras ficam "splitadas" (inline-block → quebram sozinhas no
// resize); dividir em palavras não depende das métricas da fonte → roda já no init.
const LIT_DIM = 'rgba(217, 217, 217, 0.16)'
function litOnScroll(el) {
    if (!el || REDUCED_MOTION) return
    const words = SplitText.create(el, { type: 'words', wordsClass: 'lit-word', aria: 'none' }).words
    const tween = gsap.fromTo(words, { color: LIT_DIM },
        { color: '#ffffff', ease: 'none', stagger: 0.1, duration: 0.5, paused: true })
    ScrollTrigger.create({ trigger: el, start: 'top 82%', end: 'bottom 48%', scrub: true, animation: tween })
}

// MOTION letra a letra, como no desktop: cada letra cai motionDrop px com blur que se desfaz no
// caminho e opacidade curtinha (~linear), stagger de 2 frames, chegada (0,0,0,1). Cada letra é
// uma <svg> própria (splitHeroTitle) → transform + blur rodam na GPU; no fim as letras deixam
// de ser camadas (voltam a ser pintadas na faixa, que é a camada da câmera).
function motionLettersDrop() {
    const I = MOBILE_INTRO
    return gsap.timeline()
        .fromTo(heroLetters,
            { y: -I.motionDrop, filter: `blur(${I.motionBlur}px)`, willChange: 'transform, filter' },
            { y: 0, filter: 'blur(0px)', duration: 1, ease: ARRIVE, stagger: 2 / 60, clearProps: 'filter,willChange' },
            0)
        .fromTo(heroLetters, { opacity: 0 }, { opacity: 1, duration: 0.08, ease: 'none', stagger: 2 / 60 }, 0)
}

// Travessia: a faixa do MOTION (~2,2 telas de largura) passa pela tela como uma câmera — de
// "MO" encostado na margem esquerda até o N encostado na direita ("ION") —, guiada pelo scroll:
// segura, anda, segura (ease-in-out) e ainda desliza um pouco quando o dedo para (scrub com
// inércia = follow-through). Sem scroll, nada se mexe. Começa no 1º pixel de scroll (a faixa já
// está inteira na 1ª tela) e termina com a base dela em 20% da tela. O ScrollTrigger anima um
// PROXY (o refresh dele reverteria a transform do alvo); o x da faixa é escrito à parte
// (quickSetter). Recriável (?debug: inércia).
const MOTION_CAM = { end: 0.2, lag: 0.5 }
const CAM_EASE = makeCubicBezier(0.65, 0, 0.35, 1)
let motionCam = null
function initMotionCamera() {
    if (!IS_MOBILE || REDUCED_MOTION || !heroTitleEl || !heroMotionEl) return
    motionCam?.scrollTrigger?.kill()
    motionCam?.kill()
    const setX = gsap.quickSetter(heroTitleEl, 'x', 'px')
    const cam = { p: 0 }
    let travel = 0
    const measure = () => {   // quanto a palavra passa da largura útil (tela − margens de 18px)
        const margin = parseFloat(getComputedStyle(heroTitleEl).marginLeft) || 0
        travel = Math.max(0, heroTitleEl.getBoundingClientRect().width - (heroMotionEl.clientWidth - 2 * margin))
    }
    const apply = () => setX(-travel * cam.p)
    measure()
    motionCam = gsap.to(cam, {
        p: 1,
        ease: CAM_EASE,
        onUpdate: apply,
        scrollTrigger: {
            trigger: heroMotionEl,
            start: 0,
            end: () => `bottom ${MOTION_CAM.end * 100}%`,
            scrub: MOTION_CAM.lag || true,
            onRefresh: () => { measure(); apply() },
        },
    })
    apply()
}

// A tela "acende" já do começo do reel (a luz dela tinge a ilha junto — ver applyMobileHero).
function wakeScreen() {
    try { screenVideoEl.currentTime = 0 } catch (e) {}
    playVideo(screenVideoEl)
}

// Scroll: travado desde o loader (holdScroll, seção SCROLL) até a entrada da ilha. Tentou rolar
// depois do corte → a coreografia acelera (×3) e o scroll volta na hora (nunca prende
// ninguém). O Lenis emite 'virtual-scroll' mesmo parado.
let offIntroScroll = null
function armScrollSkip() {
    offIntroScroll?.()
    offIntroScroll = lenis.on('virtual-scroll', ({ deltaX, deltaY }) => {
        if (!deltaX && !deltaY) return   // toque parado (tap) não conta
        introTl?.timeScale(3)
        unlockIntroScroll()
    })
}
function unlockIntroScroll() {
    offIntroScroll?.()
    offIntroScroll = null
    releaseScroll()
}

function finishMobileIntro() {
    unlockIntroScroll()
    if (mobileIntroFinished) return
    mobileIntroFinished = true
    resolveMobileIntroDone?.()              // libera os Rives do Work + prefetch dos .riv
}

// Chamado no corte do loader (startSiteIntro) e de novo, com late = true, pelo
// loadingManager.onLoad se a ilha chegar depois do fallback de 6s (aí só ela entra).
function playMobileIntro(late = false) {
    const I = MOBILE_INTRO

    // rede lenta: o site revelou sem a ilha → o MOTION entra já e o scroll fica livre; a ilha
    // entra quando os GLBs chegarem (ver loadingManager.onLoad)
    if (!assets3DReady) {
        mobileAwait3D = true
        // estacionada ACIMA da tela (nada "pisca" se um GLB chegar antes do outro): a câmera só
        // desce com o scroll — por baixo, a ilha apareceria no pé de quem rolasse
        mobileHero.y = -1
        releaseScroll()
        if (REDUCED_MOTION) gsap.to(heroLetters, { opacity: 1, duration: 0.6, ease: 'power1.out' })
        else motionLettersDrop()
        if (captionReveal) gsap.delayedCall(I.captionAt, captionReveal)
        gsap.delayedCall(1.5, finishMobileIntro)
        return
    }
    introTl?.kill()

    if (REDUCED_MOTION) {                        // "reduzir movimento": tudo no lugar, com fades
        Object.assign(mobileHero, { y: fitCache.restY, scale: 1, power: 1, float: 0, shadow: 0 })
        introOffset.y = 0
        if (lid) lid.rotation.x = LID_OPEN
        wakeScreen()
        introTl = gsap.timeline({ onComplete: finishMobileIntro })
            .from(canvas, { opacity: 0, duration: 0.8, ease: 'power1.out' }, 0)
            .to(mobileHero, { shadow: 1, duration: 0.8, ease: 'power1.out' }, 0)
        if (!late) {
            introTl.to(heroLetters, { opacity: 1, duration: 0.8, ease: 'power1.out' }, 0.2)
            releaseScroll()
        }
        return
    }

    // Pose inicial aplicada JÁ, no frame do corte (sob o loader a ilha repousava no lugar, só
    // p/ compilar shaders/texturas): como no desktop, a ilha NÃO se desloca — começa girada e
    // um pouco menor, com a tampa fechada e a tela apagada; gira e cresce até a pose default
    // (curva ISLAND_IN, a do desktop) enquanto o Mac abre (a animação de tampa do desktop).
    mobileHero.y      = fitCache.restY
    mobileHero.scale  = I.startScale
    mobileHero.power  = 0
    mobileHero.float  = 0
    mobileHero.shadow = 0
    introOffset.y = I.startSpin
    if (lid) { gsap.killTweensOf(lid.rotation); lid.rotation.x = 0 }

    // (a sombra no MOTION aparece junto com a ilha; o "fim" é quando a tela já acendeu — a
    //  flutuação segue depois)
    introTl = gsap.timeline()
        .to(introOffset, { y: 0, duration: I.spinDur, ease: ISLAND_IN }, 0)
        .to(mobileHero, { scale: 1, shadow: 1, duration: I.spinDur, ease: ISLAND_IN }, 0)
    if (lid) introTl.to(lid.rotation, { x: LID_OPEN, duration: lidParams.duration, ease: makeLidEase() }, lidParams.delay)
    introTl
        .call(playVideo, [screenVideoEl], I.screenAt)
        .call(wakeScreen, null, I.wakeAt)
        .to(mobileHero, { power: 1, duration: I.powerDur, ease: 'none' }, I.wakeAt)
        .to(mobileHero, { float: 1, duration: 2.5, ease: 'sine.inOut' }, I.floatAt)
        .call(finishMobileIntro, null, I.endAt)
    if (!late) {
        introTl
            .add(motionLettersDrop(), 0)
            .call(() => captionReveal?.(), null, I.captionAt)
            .fromTo('nav', { opacity: 0, y: -6 }, { opacity: 1, y: 0, duration: 1.2, ease: ARRIVE, clearProps: 'opacity,transform' }, 0.1)
            .call(unlockIntroScroll, null, I.unlockAt)
        armScrollSkip()
    }
}

// ?debug: recomeça a coreografia do zero (p/ ajustar os tempos no celular) — a dica de scroll
// volta junto, no mesmo tempo de uma visita nova
function replayMobileIntro() {
    introTl?.kill()
    resetScrollHint()
    lenis.scrollTo(0, { immediate: true, force: true })
    holdScroll()
    playMobileIntro()
    scheduleScrollHint(MOBILE_INTRO.endAt + SCROLL_HINT.wait)
}

// Dica de scroll: parado no hero, sem nunca ter rolado → a página desce um pouco sozinha e volta
// ("dá pra rolar"), como os apps fazem. É um scroll de verdade (lenis.scrollTo): a ilha sobe, a
// faixa do MOTION anda um pouco, a sombra esmaece e o texto começa a acender — a dica mostra o
// próprio efeito do scroll. A contagem começa no fim da coreografia e qualquer toque/tecla a
// recomeça (quem está girando a ilha não leva a dica no meio do gesto). Rolou de verdade uma vez →
// nunca mais. Tocar durante a dica segura a página onde está (como um scroll do iOS "pego" pelo
// dedo) e o user assume. Contagem em setTimeout, não delayedCall: com lagSmoothing(0) o relógio
// do GSAP pula o tempo da aba escondida (um delayedCall criado na volta dispararia na hora).
const SCROLL_HINT = {
    wait:  3,      // s parado no hero até a dica (do fim da coreografia ou do último toque)
    again: 6,      // s até repetir, se continuar parado
    times: 2,      // quantas vezes, no máximo
    dist:  80,     // px que a página desce
    down:  0.6,    hold: 0.15,    back: 0.9,   // s: ida, pausa, volta
}
const HINT_DOWN = makeCubicBezier(0.4, 0, 0.2, 1)     // sai devagar, pousa longo
const HINT_BACK = makeCubicBezier(0.65, 0, 0.35, 1)   // segura, anda, segura (a curva da câmera)
const hint = { armed: false, off: false, shown: 0, playing: false, touching: false, timer: 0 }

function scheduleScrollHint(delay = SCROLL_HINT.wait) {
    if (hint.playing) return   // (durante a dica o timer é a pausa antes da volta)
    clearTimeout(hint.timer)
    if (!hint.armed || hint.off || hint.shown >= SCROLL_HINT.times) return
    hint.timer = setTimeout(playScrollHint, delay * 1000)
}

function playScrollHint() {
    // só no topo, sem dedo na tela, com o scroll livre (sem modal, drag da ilha ou trava), a ilha
    // já no lugar (numa rede lenta ela pode ainda estar entrando) e a aba visível — senão, conta de novo
    if (scrollY > 1 || hint.touching || lenis.isStopped || introTl?.isActive() || document.hidden) {
        scheduleScrollHint()
        return
    }
    const H = SCROLL_HINT
    hint.shown++
    hint.playing = true
    lenis.scrollTo(H.dist, {
        duration: H.down, easing: HINT_DOWN,
        onComplete: () => {
            hint.timer = setTimeout(() => {
                if (lenis.isStopped) { hint.playing = false; return }   // algo parou o scroll na pausa
                lenis.scrollTo(0, {
                    duration: H.back, easing: HINT_BACK,
                    onComplete: () => { hint.playing = false; scheduleScrollHint(H.again) },
                })
            }, H.hold * 1000)
        },
    })
}

// O user assumiu (rolou, ou tocou durante a dica): não avisa mais. No meio da dica a página para
// onde está — no toque o Lenis (syncTouch) já faz isso sozinho; o stop/start cobre o mouse.
function stopScrollHint() {
    clearTimeout(hint.timer)
    hint.off = true
    if (hint.playing && !lenis.isStopped) { lenis.stop(); lenis.start() }
    hint.playing = false
}

// ?debug: como numa visita nova (↺ replay, "▶ ver a dica de scroll")
function resetScrollHint() {
    clearTimeout(hint.timer)
    Object.assign(hint, { off: false, shown: 0, playing: false, touching: false })
}
function previewScrollHint() {
    resetScrollHint()
    hint.armed = true
    lenis.scrollTo(0, { immediate: true, force: true })
    playScrollHint()
}

function initScrollHint() {
    if (REDUCED_MOTION) return   // scroll automático é justamente o movimento a evitar
    // a página mexeu sem ser a dica (dedo, link do nav, teclado) → já sabe rolar. Não usa o
    // 'virtual-scroll': ele dispara também ao girar a ilha (com o Lenis parado).
    lenis.on('scroll', ({ scroll }) => { if (scroll > 1 && !hint.playing && !hint.off) stopScrollHint() })
    const onInput = (e) => {
        if (e.target instanceof Element && e.target.closest('.lil-gui')) return   // painel do ?debug
        const down = e.type === 'pointerdown'
        if (e.type !== 'keydown') hint.touching = down
        if (hint.playing && (down || e.type === 'keydown')) stopScrollHint()
        else scheduleScrollHint()
    }
    for (const type of ['pointerdown', 'pointerup', 'pointercancel', 'keydown']) {
        window.addEventListener(type, onInput, { capture: true, passive: true })
    }
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) { scheduleScrollHint(); return }
        clearTimeout(hint.timer)
        // aba escondida no meio da dica → volta ao topo já (ninguém vê)
        if (hint.playing) { hint.playing = false; lenis.scrollTo(0, { immediate: true, force: true }) }
    })
    mobileIntroDone.then(() => { hint.armed = true; scheduleScrollHint() })
}

// Labels/headlines: dobradiça 1x quando entram na tela; no fim desfaz o split.
function hingeOnEnter(el, start = 'top 88%') {
    if (!el || REDUCED_MOTION) return
    const split = SplitText.create(el, { type: 'lines', mask: 'lines', linesClass: 'hinge-line', aria: 'none' })
    gsap.set(split.lines, HINGE_FROM)
    ScrollTrigger.create({
        trigger: el, start, once: true,
        onEnter: () => gsap.to(split.lines, {
            ...HINGE_TO, duration: 1.4, ease: ARRIVE, stagger: 0.08,
            onComplete: () => split.revert(),
        }),
    })
}

// Legenda do hero: a mesma dobradiça, direto nos spans (1 linha cada; a própria linha é a
// máscara — .hinge-mask). Sem SplitText: o revert() recria o HTML e o relógio perderia o <time>.
// Já na 1ª tela (o MOTION sobe até a ilha) → entra na coreografia, logo depois das letras do
// MOTION (captionAt); senão, quando aparecer no scroll.
let captionReveal = null
function hingeCaption() {
    const cap = document.querySelector('.hero-caption')
    const spans = gsap.utils.toArray('.hero-caption > span')
    if (!cap || !spans.length || REDUCED_MOTION) return
    gsap.set(spans, HINGE_FROM)
    const reveal = () => gsap.fromTo(spans, HINGE_FROM,
        { ...HINGE_TO, duration: 1.4, ease: ARRIVE, stagger: 0.08, clearProps: 'transform' })
    if (cap.getBoundingClientRect().top < window.innerHeight) { captionReveal = reveal; return }
    ScrollTrigger.create({ trigger: cap, start: 'top 88%', once: true, onEnter: reveal })
}

// Services (lista nativa — o Rive é só do desktop)
function initServicesMobile() {
    const items = gsap.utils.toArray('.service')
    if (!items.length) return
    const names = items.map((li) => li.querySelector('.service-name'))
    const idx   = items.map((li) => li.querySelector('.service-index'))
    const lines = gsap.utils.toArray('.service-line')

    // entrada (1x): hairlines se desenham da esquerda + itens em dobradiça, em cascata
    if (!REDUCED_MOTION) {
        gsap.set(items, { '--rule': 0 })
        gsap.set(lines, HINGE_FROM)
        gsap.set(idx, { opacity: 0 })
        ScrollTrigger.create({
            trigger: '.services-list', start: 'top 85%', once: true,
            onEnter: () => {
                gsap.to(items, { '--rule': 1, duration: 1.3, ease: ARRIVE, stagger: 0.1 })
                gsap.to(lines, { ...HINGE_TO, duration: 1.5, ease: ARRIVE, stagger: 0.07 })
                gsap.to(idx, { opacity: 1, duration: 0.9, ease: 'power1.out', stagger: 0.1, delay: 0.25 })
            },
        })
    }

    // rack focus: o item mais perto do centro da tela fica branco, nítido e mais encorpado;
    // os outros, desfocados e apagados. Centros medidos no <li> (nunca recebe transform) e
    // cacheados (recalculados a cada refresh do ScrollTrigger); só escreve o que mudou.
    let centers = []
    const last = items.map(() => ({}))
    const measure = () => {
        centers = items.map((li) => { const r = li.getBoundingClientRect(); return r.top + window.scrollY + r.height / 2 })
    }
    const focus = () => {
        const mid   = window.scrollY + window.innerHeight * 0.5
        const range = window.innerHeight * 0.32
        items.forEach((li, i) => {
            const d = Math.min(Math.abs(centers[i] - mid) / range, 1)
            const f = 1 - d * d * (3 - 2 * d)                          // smoothstep: 1 no centro → 0 longe
            const o = Math.round((0.18 + 0.82 * f) * 50) / 50
            const b = Math.round((1 - f) * 3 * 4) / 4                  // blur 0 → 3px, em passos de 0.25
            const w = Math.round((300 + 140 * f) / 10) * 10            // peso 300 → 440 (Geist variável)
            const c = (0.35 + 0.65 * f).toFixed(2)
            const L = last[i], n = names[i]
            if (L.o !== o) { n.style.opacity = o; L.o = o }
            if (L.b !== b) { n.style.filter = b ? `blur(${b}px)` : 'none'; L.b = b }
            if (L.w !== w) { n.style.fontWeight = w; L.w = w }
            if (L.c !== c) { idx[i].style.color = `rgba(217, 217, 217, ${c})`; L.c = c }
        })
    }
    measure()
    focus()
    ScrollTrigger.addEventListener('refresh', () => { measure(); focus() })
    lenis.on('scroll', focus)
}

// Textos (1º parágrafo e citação): divididos em palavras já, bem antes do loader sair; as
// posições dos ScrollTriggers são medidas de novo no refresh (depois da fonte).
if (IS_MOBILE) {
    litOnScroll(document.querySelector('.bio-text-left'))
    litOnScroll(document.querySelector('.bio-text-right'))
    initMotionCamera()
    hingeCaption()
    initScrollHint()
}

// O resto (quebra de linha / alturas) espera a Geist: fonts.load resolve quando ESTA fonte
// carrega, sem esperar a página toda (o document.fonts.ready do Safari só resolve depois do
// evento load — GLBs, gtag…); teto de 2s por segurança.
const geistReady = Promise.race([
    document.fonts?.load('400 16px Geist').catch(() => {}) ?? Promise.resolve(),
    new Promise((r) => setTimeout(r, 2000)),
])
if (IS_MOBILE) geistReady.then(() => {
    fitIslandMobile()                        // o nav já com a fonte certa → mede a faixa livre de novo
    initServicesMobile()
    hingeOnEnter(document.querySelector('#services .section-label'))
    hingeOnEnter(document.querySelector('#work .section-label'))
    hingeOnEnter(document.querySelector('.footer-headline'), 'top 90%')
    ScrollTrigger.refresh()
})

// ?debug: tempos da coreografia ao vivo, ↺ replay, contador de frames lentos e
// window.__intro (seek/replay) p/ conferir quadros exatos.
if (IS_MOBILE && DEBUG) {
    const perf = { slowFrames: 0 }
    gsap.ticker.add((time, dt) => { if (introTl?.isActive() && dt > 20) perf.slowFrames++ })
    const f = gui.addFolder('Intro mobile')
    f.add(MOBILE_INTRO, 'startSpin', -3.2, 3.2, 0.05).name('giro inicial (rad)')
    f.add(MOBILE_INTRO, 'startScale', 0.5, 1, 0.01).name('escala inicial (1 = sem)')
    f.add(MOBILE_INTRO, 'spinDur', 0.8, 5, 0.1).name('giro (s)')
    f.add(MOBILE_INTRO, 'motionDrop', 0, 360, 5).name('queda do MOTION (px)')
    f.add(MOBILE_INTRO, 'motionBlur', 0, 30, 1).name('blur do MOTION (px)')
    f.add(MOBILE_INTRO, 'unlockAt', 0.3, 3, 0.05).name('scroll destrava (s)')
    f.add(MOBILE_INTRO, 'wakeAt', 0.5, 3, 0.05).name('tela acende (s)')
    // Design do hero: mudar qualquer um re-enquadra (fitIslandMobile recalcula a ilha e o
    // --motion-lift do MOTION) e remede os ScrollTriggers (as seções de baixo mudam de lugar)
    const corpo = document.querySelector('.corpo')
    const refit = () => { fitIslandMobile(); ScrollTrigger.refresh() }
    const motionSize = { vw: parseFloat(getComputedStyle(corpo).getPropertyValue('--motion-h')) || 36 }
    const onMotionSize = (v) => { corpo?.style.setProperty('--motion-h', `${v}vw`); refit() }
    const islandPct = { pct: Math.round(MOBILE_FIT.size * 100) }
    const onIslandPct = (v) => { MOBILE_FIT.size = v / 100; refit() }
    f.add(MOBILE_FIT, 'motionGap', -200, 300, 1).name('ilha → MOTION (px)').onChange(refit)
    f.add(MOBILE_FIT, 'raise', -200, 200, 1).name('ilha: sobe (px)').onChange(refit)
    f.add(islandPct, 'pct', 60, 140, 1).name('ilha: tamanho (%)').onChange(onIslandPct)
    f.add(MOBILE_FIT, 'shadow', 0, 1.6, 0.05).name('sombra (força)')
    f.add(lenis.options, 'touchInertiaExponent', 1.4, 2.2, 0.01).name('scroll: deslize (toque)')
    f.add(lenis.options, 'syncTouchLerp', 0.03, 0.2, 0.005).name('scroll: suavidade (toque)')
    f.add(motionSize, 'vw', 25, 70, 1).name('MOTION: tamanho (% da tela)').onChange(onMotionSize)
    f.add(MOTION_CAM, 'end', 0, 0.6, 0.01).name('câmera: fim (tela)').onChange(() => ScrollTrigger.refresh())
    f.add(MOTION_CAM, 'lag', 0, 1.5, 0.05).name('câmera: inércia (s)').onChange(() => {
        initMotionCamera()
        ScrollTrigger.refresh()
    })
    f.add({ replay: replayMobileIntro }, 'replay').name('↺ replay')
    f.add(perf, 'slowFrames').name('frames >20ms').listen()
    f.add(autoplayStatus, 'estado').name('autoplay de vídeo').listen().disable()   // "bloqueado" = Modo Pouca Energia / aparelho quente

    const hf = gui.addFolder('Dica de scroll')
    hf.add(SCROLL_HINT, 'wait', 1, 10, 0.5).name('espera (s)')
    hf.add(SCROLL_HINT, 'again', 2, 20, 0.5).name('repete após (s)')
    hf.add(SCROLL_HINT, 'times', 0, 3, 1).name('vezes')
    hf.add(SCROLL_HINT, 'dist', 20, 200, 5).name('distância (px)')
    hf.add(SCROLL_HINT, 'down', 0.2, 1.5, 0.05).name('ida (s)')
    hf.add(SCROLL_HINT, 'hold', 0, 1, 0.05).name('pausa (s)')
    hf.add(SCROLL_HINT, 'back', 0.3, 2, 0.05).name('volta (s)')
    hf.add({ preview: previewScrollHint }, 'preview').name('▶ ver a dica de scroll')
    hf.close()

    // Painel enxuto no celular: só o que mexe no DESIGN do hero (layout, tamanhos, sombra) e a
    // dica de scroll, na raiz e já aberto — no meio dos outros controles o Mateus se perdia. As
    // pastas somem por CSS, inclusive as criadas depois, quando os GLBs carregam. ?debug=all
    // mostra todas.
    if (new URLSearchParams(location.search).get('debug') !== 'all') {
        const lean = document.createElement('style')
        lean.textContent = '.lil-gui.root.gui-lean > .children > .lil-gui { display: none; }'
        document.head.appendChild(lean)
        gui.domElement.classList.add('gui-lean')
        gui.add(MOBILE_FIT, 'motionGap', -200, 300, 1).name('ilha → MOTION (px)').onChange(refit)
        gui.add(motionSize, 'vw', 25, 70, 1).name('MOTION: tamanho (% da tela)').onChange(onMotionSize)
        gui.add(MOBILE_FIT, 'raise', -200, 200, 1).name('ilha: sobe (px)').onChange(refit)
        gui.add(islandPct, 'pct', 60, 140, 1).name('ilha: tamanho (%)').onChange(onIslandPct)
        gui.add(MOBILE_FIT, 'shadow', 0, 1.6, 0.05).name('sombra: força')
        gui.add(SCROLL_HINT, 'wait', 1, 10, 0.5).name('dica de scroll: espera (s)')
        gui.add(SCROLL_HINT, 'dist', 20, 200, 5).name('dica de scroll: distância (px)')
        gui.add({ preview: previewScrollHint }, 'preview').name('▶ ver a dica de scroll')
        gui.title('Ajuste')
        gui.open()
    }
    window.__intro = {
        seek: (t) => { introTl?.pause(); introTl?.seek(t) },
        play: () => introTl?.play(),
        replay: replayMobileIntro,
        get tl() { return introTl },
    }
}


// ─────────────────────────────────────────────────────────────
// RIVE — TYPE OF WORK (section "Services")
// Scroll normal (sem pin): o number "scroll" do ViewModel1 vai de 0→5, mapeando
// a posição do CENTRO da viewport ao longo do rive (0 = topo do rive no centro,
// 5 = base no centro). Assim o quinto destacado é sempre o que está no centro.
// ─────────────────────────────────────────────────────────────
function initWorkRives({ Rive, Layout, Fit }) {
    const TOW_MIN = 0   // 0 = topo do rive no centro (nada selecionado)
    const TOW_MAX = 5   // 5 = base do rive no centro (nada selecionado)

    // PERF: cada .riv é criado SÓ quando o card se aproxima da viewport (o poster
    // /1º-frame cobre até lá). Antes, os ~6.7 MB de .riv baixavam todos de uma vez
    // no load, competindo com o 3D — por isso demoravam segundos após o loading.
    const activeInstances = []   // instâncias vivas (pra resize)

    // some com o 1º-frame (poster) quando o riv termina de carregar
    const hidePoster = (canvasId) => {
        const poster = document.getElementById(canvasId)?.parentElement?.querySelector('.card-poster')
        if (poster) poster.style.opacity = '0'
    }

    // Cria a instância só quando `canvas` chega perto da viewport (loadMargin) e
    // gerencia play/pause por visibilidade (perf: não renderiza fora da tela).
    // buildConfig(get) recebe um getter da própria instância (pra usar no onLoad).
    const lazyRive = (canvas, buildConfig, loadMargin = '1200px') => {
        if (!canvas) return
        const loadIO = new IntersectionObserver((entries, obs) => {
            if (!entries.some(e => e.isIntersecting)) return
            obs.disconnect()                       // baixa/instancia uma vez só
            let inst
            inst = new Rive(buildConfig(() => inst))
            activeInstances.push(inst)
            const playIO = new IntersectionObserver((es) => {
                for (const e of es) e.isIntersecting ? inst.play() : inst.pause()
            }, { rootMargin: '150px' })
            playIO.observe(canvas)
        }, { rootMargin: loadMargin })
        loadIO.observe(canvas)
    }

    // ── TYPE OF WORK (Services) — scroll-driven ──
    const towCanvas = document.getElementById('canvas-type-of-work')
    lazyRive(towCanvas, (get) => ({
        src: '/riv/type_of_work(production).riv',
        canvas: towCanvas,
        autoplay: true,
        stateMachine: 'State Machine 1',
        automaticallyHandleEvents: true,
        onLoad: () => {
            const towInstance = get()
            towInstance.resizeDrawingSurfaceToCanvas()

            const vm = towInstance.viewModelByName('ViewModel1')
            if (!vm) { console.warn("Rive: ViewModel 'ViewModel1' não encontrado (type of work production)"); return }
            const vmi = vm.defaultInstance()
            towInstance.bindViewModelInstance(vmi)

            const scrollProp = vmi?.number('scroll')
            if (!scrollProp) { console.warn("Rive: number 'scroll' não encontrado no ViewModel1"); return }
            scrollProp.value = TOW_MIN

            // posição (%) da viewport usada como referência da faixa de scroll.
            // 50 = centro da viewport. O trigger é o PRÓPRIO canvas do rive (não a
            // #services inteira), então o quinto destacado = o pedaço do rive que
            // está cruzando o centro da viewport. (Só desktop: no mobile o canvas é
            // display:none — o IntersectionObserver nunca o instancia.)
            const towParams = { pos: 50 }
            const applyTowProgress = (p) => { scrollProp.value = TOW_MIN + p * (TOW_MAX - TOW_MIN) }
            const towST = ScrollTrigger.create({
                trigger: towCanvas,
                start: () => `top ${towParams.pos}%`,
                end:   () => `bottom ${towParams.pos}%`,
                onUpdate: (self) => applyTowProgress(self.progress),
            })
            ScrollTrigger.refresh()
            // Sincroniza o valor com a posição ATUAL — sem isso, ao chegar na
            // section pela animação do nav (sem scroll manual depois), o onUpdate
            // não dispara e o número "scroll" ficava preso no valor inicial.
            applyTowProgress(towST.progress)

            // Debug: ajustar a faixa de scroll ao vivo
            const f = gui.addFolder('Type of Work (scroll)')
            f.add(towParams, 'pos', 0, 100, 1).name('posição faixa (%)').onChange(() => towST.refresh())
        },
    }), '1500px')

    // No mobile (≤768px) motion_card e dynamic_card (feitos p/ desktop) não existem: a linha
    // deles é display:none — nem instanciamos. Os demais rives seguem normais.

    // ── WORK CARDS — Fit.Cover preenche o card; .work-card (overflow+radius) mascara ──
    const motionCanvas = document.getElementById('canvas-card-motion')
    if (!IS_MOBILE) lazyRive(motionCanvas, (get) => ({
        src: '/riv/motion_card.riv',
        canvas: motionCanvas,
        autoplay: true,
        stateMachine: 'State Machine 1',
        automaticallyHandleEvents: true,
        autoBind: true,   // vincula o View Model default → habilita a interação data-bound
        layout: new Layout({ fit: Fit.Cover }),
        onLoad: () => { get().resizeDrawingSurfaceToCanvas(); hidePoster('canvas-card-motion') },
    }))

    const dynamicCanvas = document.getElementById('canvas-card-dynamic')
    if (!IS_MOBILE) lazyRive(dynamicCanvas, (get) => ({
        src: '/riv/dynamic_card.riv',
        canvas: dynamicCanvas,
        autoplay: true,
        stateMachine: 'State Machine 1',
        automaticallyHandleEvents: true,
        layout: new Layout({ fit: Fit.Cover }),
        onLoad: () => {
            const cardDynamic = get()
            cardDynamic.resizeDrawingSurfaceToCanvas()
            hidePoster('canvas-card-dynamic')

            const vm = cardDynamic.viewModelByName('ViewModel1')
            if (!vm) { console.warn("Rive: ViewModel 'ViewModel1' não encontrado (dynamic_card)"); return }
            const vmi = vm.defaultInstance()
            cardDynamic.bindViewModelInstance(vmi)   // (também habilita a interação data-bound)

            const btcPrice = vmi?.number('btcPrice')
            if (!btcPrice) { console.warn("Rive: number 'btcPrice' não encontrado no ViewModel1 (dynamic_card)"); return }

            // Preço do BTC em USD (Coinbase, keyless). Atualiza a cada 60s — pula com a aba
            // oculta (não gasta rede à toa).
            const fetchBtc = async () => {
                if (document.hidden) return
                try {
                    const res   = await fetch('https://api.coinbase.com/v2/prices/BTC-USD/spot')
                    if (!res.ok) throw new Error(`HTTP ${res.status}`)
                    const json  = await res.json()
                    const price = Number(json?.data?.amount)
                    if (Number.isFinite(price)) btcPrice.value = price
                } catch (e) { console.warn('BTC price fetch falhou:', e) }
            }
            fetchBtc()
            setInterval(fetchBtc, 60000)   // 60s
        },
    }))

    // ── HEADER (AI Widget builder) — pointerOn → cursor "pointing" ──
    const headerCanvas = document.getElementById('canvas-card-header')
    lazyRive(headerCanvas, (get) => ({
        src: '/riv/ai_widget_builder_header.riv',
        canvas: headerCanvas,
        autoplay: true,
        stateMachine: 'State Machine 1',
        automaticallyHandleEvents: true,
        layout: new Layout({ fit: Fit.Cover }),
        onLoad: () => {
            const cardHeader = get()
            cardHeader.resizeDrawingSurfaceToCanvas()
            hidePoster('canvas-card-header')

            const vm = cardHeader.viewModelByName('ViewModel1')
            if (!vm) { console.warn("Rive: ViewModel 'ViewModel1' não encontrado (header)"); return }
            const vmi = vm.defaultInstance()
            cardHeader.bindViewModelInstance(vmi)

            // Boolean "pointerOn" → cursor "pointing"
            const pointerOn = vmi?.boolean('pointerOn')
            if (pointerOn) {
                const applyRivePointer = () => {
                    isOverRivePointer = pointerOn.value
                    setCursorType((pointerOn.value || isOverClickable) ? 'pointing' : 'default')
                }
                applyRivePointer()
                pointerOn.on(applyRivePointer)
            } else {
                console.warn("Rive: boolean 'pointerOn' não encontrado no ViewModel1 (header)")
            }
        },
    }))

    // ── sample conversations (penúltima linha) + shareable (última) ──
    const sampleCanvas = document.getElementById('canvas-card-sample')
    lazyRive(sampleCanvas, (get) => ({
        src: '/riv/sample_conversations.riv',
        canvas: sampleCanvas,
        autoplay: true,
        stateMachine: 'State Machine 1',
        automaticallyHandleEvents: true,
        autoBind: true,
        layout: new Layout({ fit: Fit.Cover }),
        onLoad: () => { get().resizeDrawingSurfaceToCanvas(); hidePoster('canvas-card-sample') },
    }))

    const shareableCanvas = document.getElementById('canvas-card-shareable')
    lazyRive(shareableCanvas, (get) => ({
        src: '/riv/shareable_and_reusable.riv',
        canvas: shareableCanvas,
        autoplay: true,
        stateMachine: 'State Machine 1',
        automaticallyHandleEvents: true,
        autoBind: true,
        layout: new Layout({ fit: Fit.Cover }),
        onLoad: () => { get().resizeDrawingSurfaceToCanvas(); hidePoster('canvas-card-shareable') },
    }))

    onWidthResize(() => activeInstances.forEach(i => i.resizeDrawingSurfaceToCanvas()))
}

// ─────────────────────────────────────────────────────────────
// CTA (nav) — Rive 182×40 (ViewModel1). Clicar abre o modal de contato.
// ─────────────────────────────────────────────────────────────
const ctaCanvas = document.getElementById('cta-canvas')
if (ctaCanvas) {
    // Começa invisível; faz fade-in de 300ms quando o .riv carrega (evita "pop" abrupto)
    ctaCanvas.style.opacity = '0'
    ctaCanvas.style.transition = 'opacity 300ms ease'
}

function initCtaRive({ Rive, Layout, Fit }) {
    if (!ctaCanvas) { resolveCtaReady(); return }
    const ctaRive = new Rive({
        src: '/riv/CTA.riv',
        canvas: ctaCanvas,
        autoplay: true,
        stateMachine: 'State Machine 1',
        automaticallyHandleEvents: true,
        autoBind: true,                              // vincula ViewModel1 default (hover/press)
        layout: new Layout({ fit: Fit.Contain }),
        onLoad: () => {
            ctaRive.resizeDrawingSurfaceToCanvas()
            ctaCanvas.style.opacity = '1'
            resolveCtaReady()
        },
    })
    onWidthResize(() => ctaRive.resizeDrawingSurfaceToCanvas())
}

// Resize que muda a LARGURA. No celular a barra do navegador aparece/some no scroll e só
// muda a altura — redesenhar os Rives a cada vez (layout + canvas novo) engasgava o scroll.
function onWidthResize(cb) {
    let lastW = window.innerWidth
    window.addEventListener('resize', () => {
        if (window.innerWidth === lastW) return
        lastW = window.innerWidth
        cb()
    })
}

// ─────────────────────────────────────────────────────────────
// RIVE — início adiado
// Os rives (CTA + cards) só começam quando os assets 3D terminam de baixar
// (loadingManager.onLoad, ou o reveal de fallback). Antes disso os .riv + a
// compilação do WASM competiriam com o 3D, que é o que segura o loader.
// No mobile os cards ainda esperam o fim da coreografia do hero (mobileIntroDone).
// ─────────────────────────────────────────────────────────────
let rivesStarted = false
function startRives() {
    if (rivesStarted) return
    rivesStarted = true
    import('@rive-app/webgl2').then((rive) => {
        rive.RuntimeLoader.setWasmUrl(riveWasmUrl)   // WASM same-origin (ver import no topo)
        // try/catch separados: um erro no CTA não impede os cards (e vice-versa)
        try { initCtaRive(rive) }   catch (e) { console.error('Rive CTA failed:', e); resolveCtaReady() }
        // mobile: os cards do Work só depois da coreografia do hero (não disputam CPU/GPU
        // com ela). No desktop mobileIntroDone já nasce resolvida.
        mobileIntroDone.then(() => {
            try { initWorkRives(rive) } catch (e) { console.error('Rive cards failed:', e) }
        })
    }).catch(e => { console.error('Rive failed to load:', e); resolveCtaReady() })
}

// ─────────────────────────────────────────────────────────────
// CONTACT MODAL — abre pelo CTA, blura o resto da tela, envia via Web3Forms
// ─────────────────────────────────────────────────────────────
const contactModal  = document.getElementById('contact-modal')
const ctaBtn        = document.getElementById('cta-btn')
const contactForm   = document.getElementById('contact-form')
const contactStatus = document.getElementById('contact-status')

if (contactModal && ctaBtn) {
    let lastFocus = null
    let autoCloseTimer = null
    const setStatus = (cls, text) => {
        if (!contactStatus) return
        contactStatus.className = `contact-status ${cls}`.trim()
        contactStatus.textContent = text
    }

    const openModal = () => {
        clearTimeout(autoCloseTimer)                     // (o "fecha em 2.2s" de um envio anterior)
        setStatus('', '')                                // sem a mensagem do envio anterior
        lastFocus = document.activeElement
        contactModal.classList.add('is-open')
        contactModal.setAttribute('aria-hidden', 'false')
        lenis.stop()                                     // trava o scroll de fundo
        contactModal.querySelector('#cf-name')?.focus()
    }

    const closeModal = () => {
        clearTimeout(autoCloseTimer)
        lastFocus?.focus?.()                             // o foco sai do modal ANTES do aria-hidden
        contactModal.classList.remove('is-open')
        contactModal.setAttribute('aria-hidden', 'true')
        if (!scrollHeld) lenis.start()                   // (a coreografia do mobile ainda pode estar segurando)
    }

    ctaBtn.addEventListener('click', openModal)
    contactModal.querySelectorAll('[data-close]').forEach(el => el.addEventListener('click', closeModal))
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && contactModal.classList.contains('is-open')) closeModal()
    })

    // Envio → Web3Forms (cai no Gmail do Mateus). Sem recarregar a página.
    contactForm?.addEventListener('submit', async (e) => {
        e.preventDefault()
        const submitBtn = contactForm.querySelector('.contact-submit')

        if (!contactForm.checkValidity()) { contactForm.reportValidity(); return }

        setStatus('', 'Sending…')
        if (submitBtn) submitBtn.disabled = true

        try {
            const payload = Object.fromEntries(new FormData(contactForm).entries())
            const res = await fetch('https://api.web3forms.com/submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify(payload),
            })
            const data = await res.json()
            if (data.success) {
                contactForm.reset()
                setStatus('is-ok', "Got it! I'll be in touch soon. 🙌")
                autoCloseTimer = setTimeout(closeModal, 2200)
            } else {
                throw new Error(data.message || 'Submission failed')
            }
        } catch (err) {
            console.error('Web3Forms submit failed:', err)
            setStatus('is-error', 'Something went wrong. Try again or email me directly.')
        } finally {
            if (submitBtn) submitBtn.disabled = false
        }
    })
}

// Loop do 3D no gsap.ticker — adicionado DEPOIS do Lenis (ver "SCROLL"), então roda depois
// das timelines e do scroll em cada frame.
gsap.ticker.add(tick)

// ─────────────────────────────────────────────────────────────
// WORK (mobile): as linhas têm altura em vw — o desenho do desktop, com margens de 2.11vw.
// Com as margens de 18px do mobile elas passavam da largura (o card da direita colava na
// borda e sobrava um scroll lateral de ~2px). Aqui cada linha ganha a altura que faz os
// cards (aspect-ratio inline) + gaps caberem exatamente: h = (largura − gaps) / Σ aspect.
// ─────────────────────────────────────────────────────────────
function fitWorkRowsMobile() {
    const grid = document.querySelector('.work-grid')
    if (!grid) return
    const width = grid.clientWidth
    grid.querySelectorAll('.work-row').forEach((row) => {
        if (!row.offsetParent) return                      // linha desktop-only (display:none)
        const cards = [...row.children]
        const gap = parseFloat(getComputedStyle(row).columnGap) || 0
        const sum = cards.reduce((acc, card) => {
            const [a, b] = getComputedStyle(card).aspectRatio.split('/').map(Number)
            return acc + (a && b ? a / b : 0)
        }, 0)
        if (sum > 0) row.style.height = `${(width - gap * (cards.length - 1)) / sum}px`
    })
}
if (IS_MOBILE) {
    fitWorkRowsMobile()
    onWidthResize(() => { fitWorkRowsMobile(); ScrollTrigger.refresh() })
}

// ─────────────────────────────────────────────────────────────
// PERF: vídeos dos cards só tocam quando visíveis (lazy + pausa offscreen).
// Com preload="none", nada baixa no load da página. Vídeos opacos: só .mp4 H.264
// (≤1280px, decodificado em hardware em todo browser) — ver CLAUDE.md.
// ─────────────────────────────────────────────────────────────
const workVideos = document.querySelectorAll('.work-card video')
if (workVideos.length) {
    // 1) Pré-carrega cada vídeo só quando o card está a ~1 viewport de distância
    //    (antes todos os ~22 baixavam juntos após o reveal e saturavam a banda/CPU).
    const preloadIO = new IntersectionObserver((entries, obs) => {
        for (const e of entries) {
            if (!e.isIntersecting) continue
            const v = e.target
            obs.unobserve(v)
            // poster (1º frame, ~5KB) só agora — o atributo `poster` baixaria os 14 no load
            if (v.dataset.poster && !v.poster) v.poster = v.dataset.poster
            // se o play() (observer abaixo) já rodou, o load() abortaria a reprodução
            if (v.paused && v.readyState === 0) { v.preload = 'auto'; v.load() }
        }
    }, { rootMargin: '100% 0px' })
    workVideos.forEach(v => preloadIO.observe(v))

    // 2) Toca só quando visível; pausa fora da tela.
    const videoIO = new IntersectionObserver((entries) => {
        for (const e of entries) {
            if (e.isIntersecting) { e.target.dataset.autoplay = 'on';  playVideo(e.target) }
            else                  { e.target.dataset.autoplay = 'off'; e.target.pause() }
        }
    }, { rootMargin: '200px' })
    workVideos.forEach(v => videoIO.observe(v))
}
